import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { fmtMoney } from '../lib/format';
import { ConfirmModal } from '../components/ConfirmModal';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, PieChart, Pie, Cell,
} from 'recharts';
import {
  WhatsappLogo,
  Plus,
  Check,
  CreditCard,
  CheckCircle,
  Lightning,
  ShieldCheck,
  ArrowClockwise,
  Info,
  X,
  Trash,
  Warning,
  Phone,
  ShoppingCart,
  MagnifyingGlass,
  CurrencyDollar,
  Sparkle,
  FacebookLogo,
  CircleNotch,
  ChatCircle,
  Users,
  PencilSimple
} from '@phosphor-icons/react';

// Country codes for phone selector
const COUNTRY_CODES = [
  { code: '+52', country: 'México', flag: '🇲🇽', twilio: 'MX' },
  { code: '+1', country: 'USA/Canadá', flag: '🇺🇸', twilio: 'US' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷', twilio: 'AR' },
  { code: '+56', country: 'Chile', flag: '🇨🇱', twilio: 'CL' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴', twilio: 'CO' },
  { code: '+51', country: 'Perú', flag: '🇵🇪', twilio: 'PE' },
  { code: '+34', country: 'España', flag: '🇪🇸', twilio: 'ES' },
  { code: '+55', country: 'Brasil', flag: '🇧🇷', twilio: 'BR' },
];

// Channels Page
export function Channels() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [channels, setChannels] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sandboxInfo, setSandboxInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showOwnNumberModal, setShowOwnNumberModal] = useState(false);
  const [showAddSenderModal, setShowAddSenderModal] = useState(false);
  const [showBuyNumberModal, setShowBuyNumberModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // States
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [countryCode, setCountryCode] = useState('+52');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Buy number states
  const [searchCountry, setSearchCountry] = useState('MX');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [purchasingNumber, setPurchasingNumber] = useState(null);

  // Web Chat channel states
  const [showWebChatModal, setShowWebChatModal] = useState(false);
  const [webChatName, setWebChatName] = useState('');

  // WhatsApp profile customization states
  const [profileModal, setProfileModal] = useState(null); // channel being customized
  const [profileForm, setProfileForm] = useState({ about: '', description: '', address: '', email: '', website: '', vertical: '', logo_url: '' });
  const [profileVerticals, setProfileVerticals] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const VERTICAL_LABELS = {
    'Automotive': 'Automotriz', 'Beauty, Spa and Salon': 'Belleza, spa y salón',
    'Clothing and Apparel': 'Ropa y moda', 'Education': 'Educación',
    'Entertainment': 'Entretenimiento', 'Event Planning and Service': 'Eventos',
    'Finance and Banking': 'Finanzas y banca', 'Food and Grocery': 'Alimentos y abarrotes',
    'Public Service': 'Servicio público', 'Hotel and Lodging': 'Hotelería',
    'Medical and Health': 'Salud', 'Non-profit': 'Sin fines de lucro',
    'Professional Services': 'Servicios profesionales', 'Shopping and Retail': 'Comercio y retail',
    'Travel and Transportation': 'Viajes y transporte', 'Restaurant': 'Restaurante', 'Other': 'Otro',
  };

  const openProfileModal = async (channel) => {
    setProfileModal(channel);
    setProfileLoading(true);
    try {
      const res = await api.getChannelProfile(channel.id);
      const p = res.data.profile || {};
      setProfileVerticals(res.data.verticals || []);
      setProfileForm({
        about: p.about || '',
        description: p.description || '',
        address: p.address || '',
        email: p.emails?.[0]?.email || '',
        website: p.websites?.[0]?.website || '',
        vertical: p.vertical || '',
        logo_url: p.logo_url || '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar el perfil');
      setProfileModal(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('El logo debe ser una imagen (JPG o PNG)');
    if (file.size > 5 * 1024 * 1024) return toast.error('El logo debe pesar menos de 5MB');
    setLogoUploading(true);
    try {
      const res = await api.uploadFile(file);
      setProfileForm((prev) => ({ ...prev, logo_url: res.data.media_url }));
    } catch {
      toast.error('Error al subir el logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.updateChannelProfile(profileModal.id, profileForm);
      toast.success('Perfil de WhatsApp actualizado. Los cambios pueden tardar unos minutos en reflejarse.');
      setProfileModal(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al actualizar el perfil');
    } finally {
      setProfileSaving(false);
    }
  };

  // WhatsApp Embedded Signup states
  const [showEmbeddedSignupModal, setShowEmbeddedSignupModal] = useState(false);
  const [signupConfig, setSignupConfig] = useState(null);
  const [signingUp, setSigningUp] = useState(false);
  const [fbSdkReady, setFbSdkReady] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(null);
  const wabaIdRef = useRef(null);
  const phoneNumberIdRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Load WhatsApp Signup config and initialize Facebook SDK
  useEffect(() => {
    const loadSignupConfig = async () => {
      try {
        const res = await api.getWhatsAppSignupConfig();
        setSignupConfig(res.data);
      } catch {
        // Embedded Signup not configured - that's OK
      }
    };
    loadSignupConfig();
  }, []);

  useEffect(() => {
    if (!signupConfig?.facebook_app_id) return;

    const initFB = () => {
      if (window.FB) {
        window.FB.init({
          appId: signupConfig.facebook_app_id,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v21.0',
        });
        setFbSdkReady(true);
      }
    };

    // FB SDK might already be loaded
    if (window.FB) {
      initFB();
    } else {
      // Wait for it
      window.fbAsyncInit = initFB;
    }
  }, [signupConfig]);

  // Listen for WA_EMBEDDED_SIGNUP postMessage events from Facebook popup
  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'WA_EMBEDDED_SIGNUP') {
          if ((data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA') && data.data) {
            wabaIdRef.current = data.data.waba_id;
            phoneNumberIdRef.current = data.data.phone_number_id;
          } else if (data.event === 'CANCEL') {
            toast('Registro cancelado por el usuario', { icon: 'ℹ️' });
            setSigningUp(false);
          } else if (data.event === 'ERROR') {
            toast.error('Error durante el registro de WhatsApp Business');
            setSigningUp(false);
          }
        }
      } catch {
        // Not JSON, ignore
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const fetchData = async () => {
    try {
      const [channelsRes, agentsRes, sandboxRes] = await Promise.all([
        api.getChannels(),
        api.getAgents(),
        api.getSandboxInfo()
      ]);
      setChannels(channelsRes.data);
      setAgents(agentsRes.data);
      setSandboxInfo(sandboxRes.data);
    } catch (error) {
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Search available numbers
  const handleSearchNumbers = async () => {
    setSearchingNumbers(true);
    setAvailableNumbers([]);
    try {
      const res = await api.searchAvailableNumbers(searchCountry, searchQuery || null, null);
      setAvailableNumbers(res.data.numbers || []);
      if (res.data.numbers.length === 0) {
        toast('No se encontraron números disponibles', { icon: '📞' });
      }
    } catch (error) {
      toast.error('Error buscando números');
    } finally {
      setSearchingNumbers(false);
    }
  };

  // Purchase a number
  const handlePurchaseNumber = async (number) => {
    setPurchasingNumber(number.phone_number);
    try {
      const res = await api.purchaseNumber(number.phone_number, number.country);
      toast.success('¡Número comprado exitosamente!');
      setShowBuyNumberModal(false);
      setAvailableNumbers([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error comprando número');
    } finally {
      setPurchasingNumber(null);
    }
  };

  const validatePhoneNumber = (phone) => {
    const cleaned = phone.replace(/\s/g, '').replace(/-/g, '');
    if (!cleaned) return 'El número de teléfono es requerido';
    if (!/^\d{7,12}$/.test(cleaned)) return 'Ingresa solo los dígitos de tu número';
    return null;
  };

  const handleCreateOwnNumber = async () => {
    const phoneError = validatePhoneNumber(phoneNumber);
    if (phoneError) {
      setErrors({ phone_number: phoneError });
      return;
    }
    
    setCreating(true);
    setErrors({});
    const fullPhoneNumber = countryCode + phoneNumber.replace(/\s/g, '').replace(/-/g, '');
    
    try {
      const res = await api.createChannel({ phone_number: fullPhoneNumber, is_purchased: false });
      setShowOwnNumberModal(false);
      setPhoneNumber('');
      setCountryCode('+52');
      setSelectedChannel(res.data);
      setShowVerifyModal(true);
      toast.success('Código de verificación enviado');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al agregar canal';
      toast.error(message);
      setErrors({ general: message });
    } finally {
      setCreating(false);
    }
  };

  const handleCreateWebChannel = async () => {
    setCreating(true);
    try {
      await api.createWebChannel(webChatName.trim() || null);
      setShowWebChatModal(false);
      setWebChatName('');
      toast.success('Chat web creado correctamente');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear el chat web';
      toast.error(typeof message === 'string' ? message : 'Error al crear el chat web');
    } finally {
      setCreating(false);
    }
  };

  // WhatsApp Embedded Signup handler
  const handleEmbeddedSignup = useCallback(() => {
    const phoneError = validatePhoneNumber(phoneNumber);
    if (phoneError) {
      setErrors({ phone_number: phoneError });
      return;
    }

    if (!window.FB || !fbSdkReady) {
      toast.error('Facebook SDK no está listo. Recarga la página.');
      return;
    }

    setSigningUp(true);
    setErrors({});
    wabaIdRef.current = null;
    phoneNumberIdRef.current = null;

    const fullPhone = countryCode + phoneNumber.replace(/\s/g, '').replace(/-/g, '');

    window.FB.login(
      (response) => {
        if (response.authResponse) {
          // Wait briefly for the postMessage event to fire with waba_id/phone_number_id
          setTimeout(async () => {
            const waba_id = wabaIdRef.current;
            const phone_number_id = phoneNumberIdRef.current;

            if (!waba_id || !phone_number_id) {
              toast.error('No se recibieron los datos de WhatsApp Business. Intenta de nuevo.');
              setSigningUp(false);
              return;
            }

            try {
              const res = await api.whatsAppSignup({
                embedded_signup_code: response.authResponse.code,
                waba_id: String(waba_id),
                phone_number_id: String(phone_number_id),
                phone_number: fullPhone,
              });

              setShowEmbeddedSignupModal(false);
              setPhoneNumber('');
              setCountryCode('+52');
              if (res.data?.sender_status === 'pending_activation') {
                toast.success('¡Número conectado! Lo estamos activando — te notificaremos cuando esté listo.', { duration: 7000 });
              } else {
                toast.success('Registro enviado. Tu número está pendiente de aprobación por Meta.');
              }
              fetchData();
            } catch (error) {
              const detail = error.response?.data?.detail;
              const msg = typeof detail === 'string' ? detail : 'Error al registrar WhatsApp Business';
              toast.error(msg);
            }
            setSigningUp(false);
          }, 1500);
        } else {
          toast('Registro cancelado', { icon: 'ℹ️' });
          setSigningUp(false);
        }
      },
      {
        config_id: signupConfig.config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {
            // Prefilling the phone forces Meta down the "number in use" path when the
            // number is already registered; let the user pick WABA/number freely
            ...(signupConfig.solution_id && { solutionID: signupConfig.solution_id }),
          },
          featureType: '',
          sessionInfoVersion: 3,
        },
      }
    );
  }, [phoneNumber, countryCode, fbSdkReady, signupConfig]);

  // Refresh sender status on demand
  // Admin: Add existing Twilio sender
  const handleAddExistingSender = async () => {
    const phoneError = validatePhoneNumber(phoneNumber);
    if (phoneError) {
      setErrors({ phone_number: phoneError });
      return;
    }
    setCreating(true);
    setErrors({});
    const fullPhone = countryCode + phoneNumber.replace(/\s/g, '').replace(/-/g, '');
    try {
      await api.addExistingSender({ phone_number: fullPhone });
      setShowAddSenderModal(false);
      setPhoneNumber('');
      setCountryCode('+52');
      toast.success('Sender agregado correctamente');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar sender');
    } finally {
      setCreating(false);
    }
  };

  const handleRefreshStatus = async (channelId) => {
    setRefreshingStatus(channelId);
    try {
      const res = await api.getSenderStatus(channelId);
      if (res.data.sender_status === 'approved') {
        toast.success('¡Tu número ha sido aprobado!');
      } else {
        toast(`Estado: ${res.data.sender_status}`, { icon: 'ℹ️' });
      }
      fetchData();
    } catch {
      toast.error('Error al consultar estado');
    } finally {
      setRefreshingStatus(null);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode) {
      setErrors({ code: 'El código es requerido' });
      return;
    }
    if (verificationCode.length !== 6) {
      setErrors({ code: 'El código debe tener 6 dígitos' });
      return;
    }
    
    setVerifying(true);
    setErrors({});
    
    try {
      await api.verifyChannel(selectedChannel.id, verificationCode);
      setShowVerifyModal(false);
      setVerificationCode('');
      setSelectedChannel(null);
      toast.success('¡Canal verificado exitosamente!');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Código incorrecto';
      toast.error(message);
      setErrors({ code: message });
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!selectedChannel) return;
    try {
      await api.resendCode(selectedChannel.id);
      toast.success('Código reenviado');
    } catch (error) {
      toast.error('Error al reenviar código');
    }
  };

  const handleDelete = async () => {
    if (!selectedChannel) return;
    setDeleting(true);
    
    try {
      await api.deleteChannel(selectedChannel.id);
      setShowDeleteModal(false);
      setSelectedChannel(null);
      toast.success('Canal eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar canal');
    } finally {
      setDeleting(false);
    }
  };

  const handleConnect = async (channelId, agentId) => {
    try {
      await api.connectChannel(channelId, agentId);
      toast.success('Canal conectado al agente');
      fetchData();
    } catch (error) {
      toast.error('Error al conectar canal');
    }
  };

  const openVerifyModal = (channel) => {
    setSelectedChannel(channel);
    setVerificationCode('');
    setErrors({});
    setShowVerifyModal(true);
  };

  const openDeleteModal = (channel) => {
    setSelectedChannel(channel);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-[#09090B]">
            Canales WhatsApp
          </h1>
          <p className="text-[#52525B]">Conecta un número para que tu agente responda</p>
        </div>
        <button
          onClick={() => setShowOptionsModal(true)}
          data-testid="add-channel-btn"
          className="flex items-center gap-2 bg-[#0047FF] text-white rounded-xl px-4 py-2 font-medium hover:bg-[#0035CC] transition-colors"
        >
          <Plus size={20} />
          Agregar número
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-[#0047FF]/5 border border-[#0047FF]/20 p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-[#0047FF] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[#09090B] mb-1">¿Cómo funciona?</p>
            <p className="text-sm text-[#52525B]">
              Conecta un número de WhatsApp a tu agente IA. Cuando tus clientes envíen mensajes a ese número, 
              el agente responderá automáticamente. Puedes usar tu propio número o comprar uno nuevo.
            </p>
          </div>
        </div>
      </div>

      {/* Sandbox Warning - only visible for admin */}
      {isAdmin && sandboxInfo && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 mb-6">
          <div className="flex items-start gap-3">
            <Warning size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800 mb-1">Modo de pruebas (Sandbox) - Solo Admin</p>
              <p className="text-sm text-yellow-700">
                Sandbox activo. Envía <strong>"{sandboxInfo.sandbox_code}"</strong> al <strong>{sandboxInfo.whatsapp_number}</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Channels List */}
      {channels.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
          <WhatsappLogo size={48} className="mx-auto text-[#A1A1AA] mb-4" />
          <h3 className="font-bold text-lg text-[#09090B] mb-2">No tienes canales</h3>
          <p className="text-[#52525B] mb-6">Agrega un número de WhatsApp para que tu agente pueda responder</p>
          <button
            onClick={() => setShowOptionsModal(true)}
            className="inline-flex items-center gap-2 bg-[#0047FF] text-white rounded-xl px-6 py-3 font-medium"
          >
            <Plus size={20} />
            Agregar número
          </button>
        </div>
      ) : (
        <div className="border border-[#E4E4E7] bg-white">
          {channels.map((channel, index) => (
            <div 
              key={channel.id}
              data-testid={`channel-${channel.id}`}
              className={`p-4 ${index !== channels.length - 1 ? 'border-b border-[#E4E4E7]' : ''}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 flex items-center justify-center ${
                  channel.is_connected ? 'bg-green-500' :
                  channel.is_verified ? 'bg-[#0047FF]' : 'bg-[#A1A1AA]'
                }`}>
                  {channel.channel_type === 'web' ? (
                    <ChatCircle size={24} className="text-white" />
                  ) : (
                    <WhatsappLogo size={24} className="text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-[#09090B]">
                      {channel.channel_type === 'web' ? (channel.display_name || 'Chat web') : channel.phone_number}
                    </p>
                    {channel.channel_type === 'web' && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 font-medium">
                        Chat web
                      </span>
                    )}
                    {channel.is_purchased && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 font-medium">
                        Comprado
                      </span>
                    )}
                    {channel.registration_type === 'embedded_signup' && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 font-medium">
                        WhatsApp Business
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    {/* Sender status badges for Embedded Signup channels */}
                    {channel.sender_status === 'pending' && (
                      <span className="text-yellow-600 flex items-center gap-1 animate-pulse">
                        <CircleNotch size={14} className="animate-spin" /> Pendiente de aprobación por Meta
                      </span>
                    )}
                    {channel.sender_status === 'pending_activation' && (
                      <span className="text-yellow-600 flex items-center gap-1">
                        <CircleNotch size={14} className="animate-spin" /> Activando tu número — te notificaremos cuando esté listo
                      </span>
                    )}
                    {channel.sender_status === 'failed' && (
                      <span className="text-red-600 flex items-center gap-1">
                        <Warning size={14} /> Activación fallida — contacta a soporte
                      </span>
                    )}
                    {channel.sender_status === 'approved' && !channel.is_connected && (
                      <span className="text-[#0047FF]">Aprobado · Sin agente</span>
                    )}
                    {channel.sender_status === 'approved' && channel.is_connected && (
                      <>
                        <span className="text-green-600">Conectado</span>
                        {channel.agent_id && agents.find(a => a.id === channel.agent_id) && (
                          <span className="text-[#52525B]">· {agents.find(a => a.id === channel.agent_id).name}</span>
                        )}
                      </>
                    )}
                    {channel.sender_status === 'rejected' && (
                      <span className="text-red-600">Rechazado por Meta</span>
                    )}
                    {channel.sender_status === 'failed' && (
                      <span className="text-red-600">Error en registro</span>
                    )}
                    {/* Non-embedded-signup channels (sandbox, purchased) */}
                    {!channel.sender_status && !channel.is_verified && (
                      <span className="text-yellow-600">Pendiente de verificación</span>
                    )}
                    {!channel.sender_status && channel.is_verified && !channel.is_connected && (
                      <span className="text-[#0047FF]">Verificado · Sin agente</span>
                    )}
                    {!channel.sender_status && channel.is_connected && (
                      <>
                        <span className="text-green-600">Conectado</span>
                        {channel.agent_id && agents.find(a => a.id === channel.agent_id) && (
                          <span className="text-[#52525B]">· {agents.find(a => a.id === channel.agent_id).name}</span>
                        )}
                      </>
                    )}
                    {channel.monthly_cost && (
                      <span className="text-[#A1A1AA]">· ${channel.monthly_cost} MXN/mes</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Refresh status button for pending channels */}
                  {channel.sender_status === 'pending' && (
                    <button
                      onClick={() => handleRefreshStatus(channel.id)}
                      disabled={refreshingStatus === channel.id}
                      className="px-3 py-2 border border-[#E4E4E7] text-sm font-medium hover:bg-[#F4F4F5] flex items-center gap-1 disabled:opacity-50"
                    >
                      <ArrowClockwise size={14} className={refreshingStatus === channel.id ? 'animate-spin' : ''} />
                      Actualizar estado
                    </button>
                  )}
                  {/* Verify button for sandbox channels (no sender_status) */}
                  {!channel.sender_status && !channel.is_verified && (
                    <button
                      onClick={() => openVerifyModal(channel)}
                      className="px-3 py-2 bg-yellow-500 text-white text-sm font-medium hover:bg-yellow-600 transition-colors"
                    >
                      Verificar
                    </button>
                  )}
                  {/* Connect to agent - available for verified/approved channels */}
                  {((channel.sender_status === 'approved') || (!channel.sender_status && channel.is_verified)) && !channel.is_connected && agents.length > 0 && (
                    <select
                      onChange={(e) => e.target.value && handleConnect(channel.id, e.target.value)}
                      className="px-3 py-2 border border-[#E4E4E7] text-sm"
                    >
                      <option value="">Conectar a agente...</option>
                      {agents.map(agent => (
                        <option key={agent.id} value={agent.id}>{agent.name}</option>
                      ))}
                    </select>
                  )}
                  {channel.is_connected && (
                    <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                      <ShieldCheck size={16} /> Activo
                    </span>
                  )}
                  {channel.sender_status === 'approved' && (
                    <button
                      onClick={() => openProfileModal(channel)}
                      data-testid={`profile-btn-${channel.id}`}
                      className="px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm font-medium hover:border-[#0047FF] hover:text-[#0047FF] transition-colors flex items-center gap-1.5"
                      title="Personalizar perfil de WhatsApp"
                    >
                      <PencilSimple size={14} /> Perfil
                    </button>
                  )}
                  <button
                    onClick={() => openDeleteModal(channel)}
                    className="p-2 text-[#A1A1AA] hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Eliminar canal"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* WhatsApp Profile Customization Modal */}
      {profileModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border border-[#E4E4E7] w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-[#09090B]">Perfil de WhatsApp Business</h3>
                <p className="text-xs text-[#52525B] mt-1">{profileModal.phone_number} · así te ven tus clientes en WhatsApp</p>
              </div>
              <button onClick={() => setProfileModal(null)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>

            {profileLoading ? (
              <div className="p-12 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
              </div>
            ) : (
              <>
                <div className="p-6 space-y-4">
                  {/* Logo */}
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-[#F4F4F5] border border-[#E4E4E7] overflow-hidden flex items-center justify-center flex-shrink-0">
                      {profileForm.logo_url ? (
                        <img src={profileForm.logo_url} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <WhatsappLogo size={32} className="text-[#A1A1AA]" />
                      )}
                    </div>
                    <div>
                      <input type="file" ref={logoInputRef} onChange={handleLogoUpload} accept="image/png,image/jpeg" className="hidden" />
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploading}
                        className="px-4 py-2 border border-[#E4E4E7] rounded-xl text-sm font-medium hover:border-[#0047FF] hover:text-[#0047FF] transition-colors disabled:opacity-50"
                      >
                        {logoUploading ? 'Subiendo...' : 'Subir logo'}
                      </button>
                      <p className="text-xs text-[#A1A1AA] mt-1">Cuadrado, JPG o PNG, máx. 5MB</p>
                    </div>
                  </div>

                  <div>
                    <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                      Frase corta (about) <span className="normal-case text-[#A1A1AA]">{profileForm.about.length}/139</span>
                    </label>
                    <input
                      type="text"
                      value={profileForm.about}
                      onChange={(e) => setProfileForm({ ...profileForm, about: e.target.value.slice(0, 139) })}
                      placeholder="Ej: Facturación electrónica CFDI 4.0 fácil y rápida"
                      className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                      Descripción <span className="normal-case text-[#A1A1AA]">{profileForm.description.length}/512 · incluye tus horarios aquí</span>
                    </label>
                    <textarea
                      value={profileForm.description}
                      onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value.slice(0, 512) })}
                      rows={3}
                      placeholder="Qué hace tu negocio, horarios de atención, etc."
                      className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none resize-none"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Categoría</label>
                    <select
                      value={profileForm.vertical}
                      onChange={(e) => setProfileForm({ ...profileForm, vertical: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white"
                    >
                      <option value="">Selecciona una categoría...</option>
                      {profileVerticals.map((v) => (
                        <option key={v} value={v}>{VERTICAL_LABELS[v] || v}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Correo</label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        placeholder="contacto@tuempresa.com"
                        className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Sitio web</label>
                      <input
                        type="url"
                        value={profileForm.website}
                        onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                        placeholder="https://tuempresa.com"
                        className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Dirección</label>
                    <input
                      type="text"
                      value={profileForm.address}
                      onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value.slice(0, 256) })}
                      placeholder="Calle, ciudad, estado"
                      className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                    />
                  </div>
                </div>

                <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
                  <button
                    onClick={() => setProfileModal(null)}
                    disabled={profileSaving}
                    className="flex-1 py-2.5 border border-[#E4E4E7] rounded-xl text-[#09090B] font-medium hover:bg-[#F4F4F5] disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={profileSaving || logoUploading}
                    data-testid="profile-save"
                    className="flex-1 py-2.5 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
                  >
                    {profileSaving ? 'Guardando...' : 'Guardar perfil'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Widget Code Section */}
      {channels.some(c => c.is_connected) && (
        <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] mt-6">
          <div className="p-6 border-b border-[#E4E4E7]">
            <h3 className="font-bold text-[#09090B] mb-1">Widgets para tu sitio web</h3>
            <p className="text-sm text-[#52525B]">
              Copia y pega el código en tu sitio para activar el chat o el botón de WhatsApp.
            </p>
          </div>
          <div className="p-6 space-y-4">
            {channels.filter(c => c.is_connected).map(channel => (
              <div key={channel.id}>
                <label className="block font-mono text-xs uppercase text-[#52525B] mb-2">
                  Código para {channel.channel_type === 'web' ? (channel.display_name || 'Chat web') : channel.phone_number}
                </label>
                {channel.channel_type !== 'web' && (
                <>
                <div className="relative">
                  <pre className="bg-[#09090B] text-green-400 p-4 text-sm font-mono overflow-x-auto whitespace-pre-wrap">
{`<script src="https://api.novagente.io/widget.js"></script>

<novagente-widget
  channel="${channel.id}"
></novagente-widget>`}
                  </pre>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`<script src="https://api.novagente.io/widget.js"></script>\n\n<novagente-widget\n  channel="${channel.id}"\n></novagente-widget>`);
                      toast.success('Código copiado');
                    }}
                    className="absolute top-2 right-2 px-3 py-1 bg-[#27272A] text-white text-xs font-medium hover:bg-[#3F3F46]"
                  >
                    Copiar
                  </button>
                </div>
                <div className="mt-3 text-xs text-[#A1A1AA]">
                  <p className="font-medium text-[#52525B] mb-1">Atributos opcionales:</p>
                  <div className="overflow-x-auto"><table className="w-full text-left">
                    <tbody>
                      <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">color</td><td>Color del botón (default: <code>#25D366</code>)</td></tr>
                      <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">position</td><td><code>right</code> o <code>left</code> (default: right)</td></tr>
                      <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">message</td><td>Mensaje pre-llenado al abrir WhatsApp</td></tr>
                      <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">tooltip</td><td>Texto que aparece al pasar el mouse</td></tr>
                      <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">size</td><td>Tamaño del botón en px (default: 60)</td></tr>
                    </tbody>
                  </table></div>
                </div>
                </>
                )}

                <div className={channel.channel_type === 'web' ? '' : 'mt-6 pt-6 border-t border-[#E4E4E7]'}>
                  <label className="block font-mono text-xs uppercase text-[#52525B] mb-2">
                    {channel.channel_type === 'web' ? 'Chat en vivo para tu sitio web' : 'Chat en vivo (alternativa al botón de WhatsApp)'}
                  </label>
                  <div className="relative">
                    <pre className="bg-[#09090B] text-green-400 p-4 text-sm font-mono overflow-x-auto whitespace-pre-wrap">
{`<script src="https://api.novagente.io/widget.js"></script>

<novagente-chat
  channel="${channel.id}"
  title="Soporte"
  subtitle="Responde en minutos"
></novagente-chat>`}
                    </pre>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`<script src="https://api.novagente.io/widget.js"></script>\n\n<novagente-chat\n  channel="${channel.id}"\n  title="Soporte"\n  subtitle="Responde en minutos"\n></novagente-chat>`);
                        toast.success('Código copiado');
                      }}
                      className="absolute top-2 right-2 px-3 py-1 bg-[#27272A] text-white text-xs font-medium hover:bg-[#3F3F46]"
                    >
                      Copiar
                    </button>
                  </div>
                  <div className="mt-3 text-xs text-[#A1A1AA]">
                    <p className="font-medium text-[#52525B] mb-1">Atributos opcionales del chat:</p>
                    <div className="overflow-x-auto"><table className="w-full text-left">
                      <tbody>
                        <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">color</td><td>Color del botón y header (default: <code>#0047FF</code>)</td></tr>
                        <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">title</td><td>Título del chat (default: Chat)</td></tr>
                        <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">subtitle</td><td>Subtítulo bajo el título</td></tr>
                        <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">welcome</td><td>Mensaje de bienvenida dentro del chat</td></tr>
                        <tr><td className="py-1 pr-4 font-mono text-[#0047FF]">position</td><td><code>right</code> o <code>left</code></td></tr>
                      </tbody>
                    </table></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* OPTIONS MODAL - Choose between own number or buy */}
      {showOptionsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Agregar canal</h3>
              <button onClick={() => setShowOptionsModal(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-[#52525B] mb-6">¿Qué tipo de canal quieres agregar?</p>
              
              <div className="grid gap-4">
                {/* Option 1: WhatsApp Business Embedded Signup */}
                <button
                  onClick={() => {
                    if (signupConfig) {
                      setShowOptionsModal(false);
                      setShowEmbeddedSignupModal(true);
                    } else {
                      toast('Esta función estará disponible próximamente', { icon: 'ℹ️' });
                    }
                  }}
                  className={`p-4 border-2 transition-colors text-left group ${
                    signupConfig
                      ? 'border-[#E4E4E7] hover:border-[#0047FF]'
                      : 'border-[#E4E4E7] opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F4F4F5] group-hover:bg-[#0047FF]/10 flex items-center justify-center transition-colors">
                      <WhatsappLogo size={24} className="text-green-600" />
                    </div>
                    <div>
                      <p className="font-bold text-[#09090B] mb-1 flex items-center gap-2">
                        Conectar mi número con WhatsApp Business
                        {signupConfig ? (
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700">Recomendado</span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs bg-[#F4F4F5] text-[#A1A1AA]">Próximamente</span>
                        )}
                      </p>
                      <p className="text-sm text-[#52525B]">
                        Vincula tu número existente como WhatsApp Business a través de Meta.
                        Tus clientes te escribirán directamente a tu número.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Option 2: Buy Number */}
                <button
                  onClick={() => { setShowOptionsModal(false); setShowBuyNumberModal(true); handleSearchNumbers(); }}
                  className="p-4 border-2 border-[#E4E4E7] hover:border-[#0047FF] transition-colors text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F4F4F5] group-hover:bg-[#0047FF]/10 flex items-center justify-center transition-colors">
                      <ShoppingCart size={24} className="text-[#0047FF]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#09090B] mb-1">Comprar número nuevo</p>
                      <p className="text-sm text-[#52525B]">
                        Obtén un número dedicado para tu negocio.
                        Obtén un número dedicado para tu negocio. Activación inmediata.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Option 3: Web Chat */}
                <button
                  onClick={() => { setShowOptionsModal(false); setShowWebChatModal(true); }}
                  className="p-4 border-2 border-[#E4E4E7] hover:border-[#0047FF] transition-colors text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F4F4F5] group-hover:bg-[#0047FF]/10 flex items-center justify-center transition-colors">
                      <ChatCircle size={24} className="text-[#0047FF]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#09090B] mb-1 flex items-center gap-2">
                        Chat para sitio web
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 font-medium">Activación inmediata</span>
                      </p>
                      <p className="text-sm text-[#52525B]">
                        Instala un chat en vivo en tu página web. Tus visitantes pueden conversar con tu agente IA sin salir de tu sitio.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Option 3: Add existing sender - only visible for admin */}
                {isAdmin && (
                  <button
                    onClick={() => { setShowOptionsModal(false); setShowAddSenderModal(true); }}
                    className="p-4 border-2 border-[#E4E4E7] hover:border-[#0047FF] transition-colors text-left group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#F4F4F5] group-hover:bg-[#0047FF]/10 flex items-center justify-center transition-colors">
                        <ShieldCheck size={24} className="text-[#0047FF]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#09090B] mb-1">Agregar sender existente (Admin)</p>
                        <p className="text-sm text-[#52525B]">
                          Registrar un número que ya está dado de alta como WhatsApp sender en el proveedor.
                        </p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Option 4: Sandbox/Testing - only visible for admin */}
                {isAdmin && (
                  <button
                    onClick={() => { setShowOptionsModal(false); setShowOwnNumberModal(true); }}
                    className="p-4 border-2 border-dashed border-[#E4E4E7] hover:border-[#A1A1AA] transition-colors text-left group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#F4F4F5] flex items-center justify-center">
                        <Phone size={24} className="text-[#A1A1AA]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#52525B] mb-1">Sandbox / Pruebas (Admin)</p>
                        <p className="text-sm text-[#A1A1AA]">
                          Verificar un número por código para pruebas internas.
                        </p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OWN NUMBER MODAL */}
      {showOwnNumberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Usar mi número existente</h3>
              <button onClick={() => setShowOwnNumberModal(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 p-3 text-sm">
                <p className="text-yellow-800">
                  <strong>Importante:</strong> Tu número NO debe estar en WhatsApp personal. 
                  Debe ser un número dedicado para tu negocio.
                </p>
              </div>
              
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Número de teléfono <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-32 px-3 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value.replace(/\D/g, ''));
                      if (errors.phone_number) setErrors({});
                    }}
                    placeholder="3310121981"
                    className={`flex-1 px-4 py-3 border focus:outline-none ${
                      errors.phone_number ? 'border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF]'
                    }`}
                  />
                </div>
                {errors.phone_number && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <Warning size={14} /> {errors.phone_number}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-[#52525B]">
                Te enviaremos un código de verificación por WhatsApp para confirmar que tienes acceso.
              </p>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button
                onClick={() => setShowOwnNumberModal(false)}
                className="flex-1 py-2 border border-[#E4E4E7] font-medium hover:bg-[#F4F4F5]"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateOwnNumber}
                disabled={creating}
                className="flex-1 py-2 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
              >
                {creating ? 'Enviando...' : 'Enviar código'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WEB CHAT CREATION MODAL */}
      {showWebChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Crear chat para sitio web</h3>
              <button onClick={() => { setShowWebChatModal(false); setWebChatName(''); }} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-3 text-sm">
                <p className="text-blue-800">
                  Después de crearlo, conéctalo a un agente IA y copia el código del widget para instalarlo en tu sitio.
                </p>
              </div>

              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Nombre del chat <span className="text-[#A1A1AA] normal-case">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={webChatName}
                  onChange={(e) => setWebChatName(e.target.value)}
                  placeholder="Ej: Chat soporte web"
                  maxLength={50}
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                />
                <p className="text-xs text-[#A1A1AA] mt-1">
                  Solo es para identificar el canal internamente. Tus visitantes no lo verán.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button
                onClick={() => { setShowWebChatModal(false); setWebChatName(''); }}
                className="flex-1 py-2 border border-[#E4E4E7] font-medium hover:bg-[#F4F4F5]"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateWebChannel}
                disabled={creating}
                className="flex-1 py-2 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
              >
                {creating ? 'Creando...' : 'Crear chat web'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WHATSAPP EMBEDDED SIGNUP MODAL */}
      {showEmbeddedSignupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Conectar WhatsApp Business</h3>
              <button onClick={() => { setShowEmbeddedSignupModal(false); setSigningUp(false); }} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 p-3 text-sm">
                <p className="text-green-800">
                  <strong>WhatsApp Business API:</strong> Al hacer clic en "Conectar", se abrirá una ventana de Meta/Facebook
                  donde vincularás tu número como WhatsApp Business. Tu número NO debe estar en uso con WhatsApp personal.
                </p>
              </div>

              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Número de teléfono <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-32 px-3 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value.replace(/\D/g, ''));
                      if (errors.phone_number) setErrors({});
                    }}
                    placeholder="3311940891"
                    className={`flex-1 px-4 py-3 border focus:outline-none ${
                      errors.phone_number ? 'border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF]'
                    }`}
                  />
                </div>
                {errors.phone_number && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <Warning size={14} /> {errors.phone_number}
                  </p>
                )}
              </div>

              <div className="bg-[#F4F4F5] p-3 text-sm space-y-1">
                <p className="font-medium text-[#09090B]">Al conectar, se abrirá una ventana donde:</p>
                <ol className="list-decimal list-inside text-[#52525B] space-y-1">
                  <li>Iniciarás sesión con Facebook</li>
                  <li>Crearás o seleccionarás tu cuenta de WhatsApp Business</li>
                  <li>Meta verificará tu número de teléfono</li>
                </ol>
              </div>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button
                onClick={() => { setShowEmbeddedSignupModal(false); setSigningUp(false); }}
                className="flex-1 py-2 border border-[#E4E4E7] font-medium hover:bg-[#F4F4F5]"
              >
                Cancelar
              </button>
              <button
                onClick={handleEmbeddedSignup}
                disabled={signingUp || !fbSdkReady}
                className="flex-1 py-3 bg-[#1877F2] text-white font-medium hover:bg-[#166FE5] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {signingUp ? (
                  <>
                    <CircleNotch size={20} className="animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    Conectar con WhatsApp Business
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD EXISTING SENDER MODAL - Admin only */}
      {showAddSenderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Agregar sender existente</h3>
              <button onClick={() => setShowAddSenderModal(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-3 text-sm">
                <p className="text-blue-800">
                  <strong>Admin:</strong> Ingresa un número que ya está registrado como WhatsApp sender
                  en el proveedor de mensajería. Se agregará como canal activo listo para conectar a un agente.
                </p>
              </div>

              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Número de teléfono <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-32 px-3 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value.replace(/\D/g, ''));
                      if (errors.phone_number) setErrors({});
                    }}
                    placeholder="3311940891"
                    className={`flex-1 px-4 py-3 border focus:outline-none ${
                      errors.phone_number ? 'border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF]'
                    }`}
                  />
                </div>
                {errors.phone_number && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <Warning size={14} /> {errors.phone_number}
                  </p>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button
                onClick={() => setShowAddSenderModal(false)}
                className="flex-1 py-2 border border-[#E4E4E7] font-medium hover:bg-[#F4F4F5]"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddExistingSender}
                disabled={creating}
                className="flex-1 py-2 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
              >
                {creating ? 'Agregando...' : 'Agregar sender'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BUY NUMBER MODAL */}
      {showBuyNumberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Comprar número nuevo</h3>
              <button onClick={() => { setShowBuyNumberModal(false); setAvailableNumbers([]); }} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            
            {/* Search */}
            <div className="p-6 border-b border-[#E4E4E7]">
              <div className="flex gap-3">
                <select
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                  className="px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.twilio} value={c.twilio}>{c.flag} {c.country}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por dígitos (opcional)"
                  className="flex-1 px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                />
                <button
                  onClick={handleSearchNumbers}
                  disabled={searchingNumbers}
                  className="px-6 py-3 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50 flex items-center gap-2"
                >
                  {searchingNumbers ? (
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <MagnifyingGlass size={20} />
                  )}
                  Buscar
                </button>
              </div>
            </div>
            
            {/* Results */}
            <div className="flex-1 overflow-y-auto p-6">
              {searchingNumbers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
                </div>
              ) : availableNumbers.length === 0 ? (
                <div className="text-center py-12">
                  <Phone size={48} className="mx-auto text-[#A1A1AA] mb-4" />
                  <p className="text-[#52525B]">Busca números disponibles en tu país</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableNumbers.map((num) => (
                    <div 
                      key={num.phone_number}
                      className="flex items-center justify-between p-4 border border-[#E4E4E7] rounded-xl hover:border-[#0047FF] transition-colors"
                    >
                      <div>
                        <p className="font-bold text-[#09090B] font-mono">{num.phone_number}</p>
                        <p className="text-sm text-[#52525B]">
                          {num.friendly_name}
                          {num.region && ` · ${num.region}`}
                        </p>
                        <div className="flex gap-2 mt-1">
                          {num.capabilities.sms && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700">SMS</span>
                          )}
                          {num.capabilities.voice && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700">Voz</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#09090B]">${num.monthly_cost} MXN</p>
                        <p className="text-xs text-[#A1A1AA]">por mes</p>
                        <button
                          onClick={() => handlePurchaseNumber(num)}
                          disabled={purchasingNumber === num.phone_number}
                          className="mt-2 px-4 py-2 bg-green-500 text-white rounded-xl text-sm font-medium hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                        >
                          {purchasingNumber === num.phone_number ? (
                            <>
                              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                              Comprando...
                            </>
                          ) : (
                            <>
                              <ShoppingCart size={16} />
                              Comprar
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* VERIFY MODAL */}
      {showVerifyModal && selectedChannel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Verificar número</h3>
              <button onClick={() => setShowVerifyModal(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-[#52525B]">
                Enviamos un código de 6 dígitos al número <strong>{selectedChannel.phone_number}</strong>
              </p>
              {isAdmin && sandboxInfo && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 text-sm">
                  <p className="text-yellow-800">
                    <strong>Sandbox:</strong> Envía <strong>"{sandboxInfo.sandbox_code}"</strong> al <strong>{sandboxInfo.whatsapp_number}</strong> primero.
                  </p>
                </div>
              )}
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Código de verificación <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    if (errors.code) setErrors({});
                  }}
                  placeholder="123456"
                  maxLength={6}
                  className={`w-full px-4 py-3 border text-center text-2xl tracking-widest font-mono focus:outline-none ${
                    errors.code ? 'border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF]'
                  }`}
                />
                {errors.code && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <Warning size={14} /> {errors.code}
                  </p>
                )}
              </div>
              <button onClick={handleResendCode} className="flex items-center gap-1 text-sm text-[#0047FF] hover:underline">
                <ArrowClockwise size={14} /> Reenviar código
              </button>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button onClick={() => setShowVerifyModal(false)} className="flex-1 py-2 border border-[#E4E4E7] font-medium hover:bg-[#F4F4F5]">
                Cancelar
              </button>
              <button
                onClick={handleVerify}
                disabled={verifying || verificationCode.length !== 6}
                className="flex-1 py-2 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
              >
                {verifying ? 'Verificando...' : 'Verificar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && selectedChannel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7]">
              <h3 className="font-bold text-lg text-[#09090B]">Eliminar canal</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 flex items-center justify-center">
                  <Trash size={24} className="text-red-500" />
                </div>
                <div>
                  <p className="font-medium text-[#09090B]">{selectedChannel.phone_number}</p>
                  <p className="text-sm text-[#52525B]">
                    {selectedChannel.is_purchased ? 'Número comprado' : 'Número propio'}
                  </p>
                </div>
              </div>
              <p className="text-[#52525B]">
                ¿Estás seguro de eliminar este canal? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-2 border border-[#E4E4E7] font-medium hover:bg-[#F4F4F5]">
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2 bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Billing Page - Complete Stripe Integration
export function Billing() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [billingInfo, setBillingInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showAddBalance, setShowAddBalance] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState(100);
  const [processing, setProcessing] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // {type: 'buy'|'cancel'|'deleteCard', ...}
  const { company } = useOutletContext();

  useEffect(() => {
    fetchBillingData();
    
    // Check for successful payment
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchBillingData = async () => {
    try {
      const [billingRes, transactionsRes, plansRes] = await Promise.all([
        api.getBillingInfo(),
        api.getTransactions(),
        api.getBillingPlans()
      ]);
      setBillingInfo(billingRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setPlans(plansRes.data.plans || []);
    } catch (error) {
      toast.error('Error cargando información de facturación');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const res = await api.checkPaymentStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        toast.success('¡Pago exitoso! Tu suscripción ha sido activada.');
        window.history.replaceState({}, '', '/dashboard/billing');
        fetchBillingData();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!billingInfo?.payment_methods?.length) {
      toast.error('Primero debes agregar un método de pago');
      setShowAddCard(true);
      return;
    }
    
    setProcessing(true);
    try {
      const res = await api.subscribe(planId);
      toast.success(res.data.message);
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al suscribirse');
    } finally {
      setProcessing(false);
    }
  };

  const handleBuyWithBalance = (plan) => setConfirmAction({ type: 'buy', plan });

  const handleCancelSubscription = () => setConfirmAction({ type: 'cancel' });

  const handleDeleteCard = (paymentMethod) => setConfirmAction({ type: 'deleteCard', paymentMethod });

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    setProcessing(true);
    try {
      if (confirmAction.type === 'buy') {
        const res = await api.buyPlanWithBalance(confirmAction.plan.id);
        toast.success(res.data.message || 'Plan activado con tu saldo');
      } else if (confirmAction.type === 'cancel') {
        const res = await api.cancelSubscription();
        toast.success(res.data.message);
      } else if (confirmAction.type === 'deleteCard') {
        await api.deletePaymentMethod(confirmAction.paymentMethod.id);
        toast.success('Tarjeta eliminada');
      }
      setConfirmAction(null);
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ocurrió un error, intenta de nuevo');
    } finally {
      setProcessing(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await api.setDefaultPaymentMethod(paymentMethodId);
      toast.success('Tarjeta predeterminada actualizada');
      fetchBillingData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleAddBalance = async () => {
    if (balanceAmount < 50) {
      toast.error('El monto mínimo es $50 MXN');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await api.addBalance(balanceAmount);
      toast.success(`Saldo agregado: $${balanceAmount} MXN`);
      setShowAddBalance(false);
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar saldo');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-[#09090B] mb-2">Facturación</h1>
      <p className="text-[#52525B] mb-8">Gestiona tu suscripción y métodos de pago</p>

      {/* Current Plan & Balance */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-1">Plan actual</p>
              <p className="font-bold text-2xl text-[#09090B] capitalize">{billingInfo?.plan_name || 'Gratis'}</p>
            </div>
            <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium ${
              billingInfo?.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              <Lightning size={14} />
              {billingInfo?.subscription_status === 'active' ? 'Activo' : 'Trial'}
            </span>
          </div>
          {billingInfo?.subscription?.cancel_at_period_end && (
            <p className="text-sm text-yellow-600">Se cancelará al final del período</p>
          )}
          {billingInfo?.plan_payment_method === 'balance' && billingInfo?.plan_expires_at && (
            <p className="text-sm text-[#52525B]">
              Pagado con saldo · vence el {new Date(billingInfo.plan_expires_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
          {billingInfo?.plan !== 'free' && billingInfo?.subscription_status === 'active' && billingInfo?.plan_payment_method !== 'balance' && (
            <button
              onClick={handleCancelSubscription}
              disabled={processing}
              className="text-sm text-red-500 hover:underline mt-2"
            >
              Cancelar suscripción
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-1">Saldo disponible</p>
              <p className="font-bold text-2xl text-[#09090B]">${fmtMoney(billingInfo?.balance_mxn)} <span className="text-sm font-normal text-[#52525B]">MXN</span></p>
            </div>
            <CurrencyDollar size={32} className="text-green-500" />
          </div>
          <button
            onClick={() => setShowAddBalance(true)}
            className="text-sm text-[#0047FF] hover:underline flex items-center gap-1"
          >
            <Plus size={14} /> Agregar saldo
          </button>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] mb-8">
        <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
          <h2 className="font-bold text-lg text-[#09090B]">Métodos de pago</h2>
          <button
            onClick={() => setShowAddCard(true)}
            className="flex items-center gap-2 text-[#0047FF] text-sm font-medium hover:underline"
          >
            <Plus size={16} /> Agregar tarjeta
          </button>
        </div>
        <div className="p-6">
          {billingInfo?.payment_methods?.length === 0 ? (
            <p className="text-[#52525B] text-center py-4">No tienes tarjetas guardadas</p>
          ) : (
            <div className="space-y-3">
              {billingInfo?.payment_methods?.map((pm) => (
                <div key={pm.id} className="flex items-center justify-between p-4 border border-[#E4E4E7] rounded-xl">
                  <div className="flex items-center gap-3">
                    <CreditCard size={24} className="text-[#52525B]" />
                    <div>
                      <p className="font-medium text-[#09090B] capitalize">{pm.brand} •••• {pm.last4}</p>
                      <p className="text-sm text-[#52525B]">Expira {pm.exp_month}/{pm.exp_year}</p>
                    </div>
                    {pm.is_default && (
                      <span className="px-2 py-0.5 text-xs bg-[#0047FF]/10 text-[#0047FF] font-medium">Predeterminada</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!pm.is_default && (
                      <button
                        onClick={() => handleSetDefault(pm.id)}
                        className="text-sm text-[#0047FF] hover:underline"
                      >
                        Hacer predeterminada
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteCard(pm)}
                      className="p-2 text-red-500 hover:bg-red-50"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Plans */}
      <h2 className="font-bold text-lg text-[#09090B] mb-4">Planes disponibles</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {plans.map((plan) => (
          <div key={plan.id} className={`p-6 rounded-2xl transition-all ${plan.popular ? 'bg-gradient-to-br from-[#0047FF] to-indigo-600 text-white shadow-xl shadow-[#0047FF]/25' : 'bg-white border border-[#E4E4E7] shadow-sm hover:shadow-md'}`}>
            {plan.popular && <span className="inline-block bg-white text-[#0047FF] text-xs font-bold px-3 py-1 rounded-full mb-4">POPULAR</span>}
            <h3 className={`font-bold text-xl mb-2 ${plan.popular ? 'text-white' : 'text-[#09090B]'}`}>{plan.name}</h3>
            <div className="mb-4">
              <span className={`font-black text-3xl ${plan.popular ? 'text-white' : 'text-[#09090B]'}`}>${plan.price_mxn}</span>
              <span className={plan.popular ? 'text-white/70' : 'text-[#52525B]'}>/mes</span>
            </div>
            <ul className="space-y-2 mb-6">
              {plan.features?.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <Check size={16} className={plan.popular ? 'text-white' : 'text-[#0047FF]'} />
                  <span className={plan.popular ? 'text-white' : 'text-[#52525B]'}>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => plan.id !== 'free' && handleSubscribe(plan.id)}
              disabled={processing || billingInfo?.plan === plan.id || plan.id === 'free'}
              className={`w-full py-3 font-medium rounded-xl transition-colors disabled:opacity-50 ${
                plan.popular ? 'bg-white text-[#0047FF] hover:bg-[#F4F4F5]' : 'bg-[#09090B] text-white hover:bg-[#27272A]'
              }`}
            >
              {billingInfo?.plan === plan.id ? 'Plan actual' : plan.id === 'free' ? 'Gratis' : 'Suscribirse'}
            </button>
            {plan.id !== 'free' &&
              (billingInfo?.balance_mxn || 0) >= plan.price_mxn &&
              (billingInfo?.plan !== plan.id || billingInfo?.plan_payment_method === 'balance') && (
              <button
                onClick={() => handleBuyWithBalance(plan)}
                disabled={processing}
                className={`w-full py-2 mt-2 text-sm font-medium border rounded-xl transition-colors disabled:opacity-50 ${
                  plan.popular
                    ? 'border-white/40 text-white hover:bg-white/10'
                    : 'border-[#0047FF] text-[#0047FF] hover:bg-[#0047FF]/5'
                }`}
                data-testid={`buy-with-balance-${plan.id}`}
              >
                {billingInfo?.plan === plan.id ? 'Renovar con saldo (30 días)' : 'Pagar con saldo (30 días)'}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
        <div className="p-6 border-b border-[#E4E4E7]">
          <h2 className="font-bold text-lg text-[#09090B]">Historial de movimientos</h2>
        </div>
        <div className="divide-y divide-[#E4E4E7]">
          {transactions.length === 0 ? (
            <p className="text-[#52525B] text-center py-8">No hay movimientos</p>
          ) : (
            transactions.map((tx) => (
              <div key={tx.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${
                    tx.type === 'credit' || tx.type === 'topup' ? 'bg-green-100' : 'bg-blue-100'
                  }`}>
                    {tx.type === 'credit' || tx.type === 'topup' ? (
                      <Plus size={20} className="text-green-600" />
                    ) : (
                      <CreditCard size={20} className="text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#09090B]">{tx.description}</p>
                    <p className="text-sm text-[#52525B]">{formatDate(tx.created_at)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${tx.type === 'credit' || tx.type === 'topup' ? 'text-green-600' : 'text-[#09090B]'}`}>
                    {tx.type === 'credit' || tx.type === 'topup' ? '+' : '-'}${fmtMoney(tx.amount_mxn)} MXN
                  </p>
                  <p className={`text-xs ${tx.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                    {tx.status === 'completed' ? 'Completado' : 'Pendiente'}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Card Modal */}
      {showAddCard && (
        <AddCardModal 
          onClose={() => setShowAddCard(false)} 
          onSuccess={() => { setShowAddCard(false); fetchBillingData(); }}
        />
      )}

      {/* Confirm actions modal (pay with balance / cancel subscription / delete card) */}
      <ConfirmModal
        open={!!confirmAction}
        title={
          confirmAction?.type === 'buy'
            ? (billingInfo?.plan === confirmAction.plan.id ? 'Renovar plan con saldo' : 'Pagar plan con saldo')
            : confirmAction?.type === 'cancel'
              ? 'Cancelar suscripción'
              : 'Eliminar tarjeta'
        }
        confirmLabel={
          confirmAction?.type === 'buy'
            ? (billingInfo?.plan === confirmAction.plan.id ? 'Renovar ahora' : 'Pagar ahora')
            : confirmAction?.type === 'cancel'
              ? 'Sí, cancelar'
              : 'Eliminar tarjeta'
        }
        danger={confirmAction?.type !== 'buy'}
        processing={processing}
        onConfirm={executeConfirmAction}
        onCancel={() => setConfirmAction(null)}
      >
        {confirmAction?.type === 'buy' && (
          <div className="space-y-4">
            <p className="text-sm text-[#52525B]">
              Vas a activar el plan <span className="font-bold text-[#09090B]">{confirmAction.plan.name}</span> por{' '}
              <span className="font-bold text-[#09090B]">30 días</span> usando tu saldo, sin necesidad de tarjeta ni suscripción.
            </p>
            <div className="bg-[#F4F4F5] border border-[#E4E4E7] p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#52525B]">Costo del plan</span>
                <span className="font-mono font-medium text-[#09090B]">−${fmtMoney(confirmAction.plan.price_mxn)} MXN</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#52525B]">Saldo actual</span>
                <span className="font-mono text-[#09090B]">${fmtMoney(billingInfo?.balance_mxn)} MXN</span>
              </div>
              <div className="border-t border-[#E4E4E7] pt-2 flex justify-between text-sm">
                <span className="font-medium text-[#09090B]">Saldo restante</span>
                <span className="font-mono font-bold text-[#09090B]">
                  ${fmtMoney((billingInfo?.balance_mxn || 0) - confirmAction.plan.price_mxn)} MXN
                </span>
              </div>
            </div>
            <p className="text-xs text-[#A1A1AA]">
              {billingInfo?.plan === confirmAction.plan.id && billingInfo?.plan_expires_at
                ? 'La renovación extiende 30 días a partir de tu vencimiento actual.'
                : 'Al terminar los 30 días, tu cuenta volverá al plan Gratis si no renuevas.'}
            </p>
          </div>
        )}
        {confirmAction?.type === 'cancel' && (
          <div className="space-y-3">
            <p className="text-sm text-[#52525B]">
              Tu suscripción al plan <span className="font-bold text-[#09090B] capitalize">{billingInfo?.plan_name}</span> se
              cancelará <span className="font-medium text-[#09090B]">al final del período actual</span>. Hasta entonces conservas todos los beneficios.
            </p>
            <p className="text-xs text-[#A1A1AA]">Puedes volver a suscribirte en cualquier momento.</p>
          </div>
        )}
        {confirmAction?.type === 'deleteCard' && (
          <div className="space-y-3">
            <p className="text-sm text-[#52525B]">
              Vas a eliminar la tarjeta{' '}
              <span className="font-medium text-[#09090B] capitalize">
                {confirmAction.paymentMethod.brand} •••• {confirmAction.paymentMethod.last4}
              </span>.
            </p>
            {confirmAction.paymentMethod.is_default && (
              <div className="bg-yellow-50 border border-yellow-200 p-3">
                <p className="text-sm text-yellow-700">Esta es tu tarjeta predeterminada. Si tienes una suscripción activa, agrega otra tarjeta para evitar fallos de cobro.</p>
              </div>
            )}
          </div>
        )}
      </ConfirmModal>

      {/* Add Balance Modal */}
      {showAddBalance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-lg text-[#09090B]">Agregar saldo</h3>
              <button onClick={() => setShowAddBalance(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block font-mono text-xs uppercase text-[#52525B] mb-2">Monto (MXN)</label>
                <input
                  type="number"
                  min="50"
                  step="50"
                  value={balanceAmount}
                  onChange={(e) => setBalanceAmount(Number(e.target.value))}
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-2xl font-bold"
                />
                <p className="text-sm text-[#52525B] mt-1">Mínimo $50 MXN</p>
              </div>
              <div className="flex gap-2">
                {[100, 200, 500, 1000].map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setBalanceAmount(amt)}
                    className={`flex-1 py-2 border font-medium ${
                      balanceAmount === amt ? 'border-[#0047FF] bg-[#0047FF]/10 text-[#0047FF]' : 'border-[#E4E4E7]'
                    }`}
                  >
                    ${amt}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button onClick={() => setShowAddBalance(false)} className="flex-1 py-3 border border-[#E4E4E7] font-medium hover:bg-[#F4F4F5]">
                Cancelar
              </button>
              <button
                onClick={handleAddBalance}
                disabled={processing || !billingInfo?.payment_methods?.length}
                className="flex-1 py-3 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
              >
                {processing ? 'Procesando...' : `Pagar $${balanceAmount} MXN`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add Card Modal Component
function AddCardModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    createSetupIntent();
  }, []);

  const createSetupIntent = async () => {
    try {
      const res = await api.createSetupIntent();
      setClientSecret(res.data.client_secret);
    } catch (error) {
      toast.error('Error al inicializar');
      onClose();
    }
  };

  if (!clientSecret) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-8">
          <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
          <h3 className="font-bold text-lg text-[#09090B]">Agregar tarjeta</h3>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#09090B]">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">
          <StripeCardForm clientSecret={clientSecret} onSuccess={onSuccess} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

// Stripe Card Form Component using Stripe Elements (secure iframe)
function StripeCardForm({ clientSecret, onSuccess, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardReady, setCardReady] = useState(false);
  const cardElementRef = useRef(null);
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);

  useEffect(() => {
    if (!window.Stripe) return;

    const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    stripeRef.current = stripe;

    const elements = stripe.elements({ clientSecret });
    elementsRef.current = elements;

    const cardElement = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          fontFamily: 'ui-monospace, monospace',
          color: '#09090B',
          '::placeholder': { color: '#A1A1AA' },
        },
        invalid: { color: '#ef4444' },
      },
    });

    cardElement.mount('#stripe-card-element');
    cardElement.on('ready', () => setCardReady(true));
    cardElement.on('change', (event) => {
      setError(event.error ? event.error.message : null);
    });
    cardElementRef.current = cardElement;

    return () => {
      cardElement.unmount();
    };
  }, [clientSecret]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripeRef.current || !cardElementRef.current) return;

    setLoading(true);
    setError(null);

    try {
      const { setupIntent, error: stripeError } = await stripeRef.current.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElementRef.current,
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      await api.addPaymentMethod(setupIntent.payment_method);
      toast.success('Tarjeta agregada!');
      onSuccess();
    } catch (err) {
      setError(err.message || 'Error al procesar la tarjeta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-3 text-sm flex items-center gap-2">
          <Warning size={16} /> {error}
        </div>
      )}

      <div>
        <label className="block font-mono text-xs uppercase text-[#52525B] mb-2">Datos de tarjeta</label>
        <div
          id="stripe-card-element"
          className="px-4 py-3 border border-[#E4E4E7] focus-within:border-[#0047FF] bg-white min-h-[44px]"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onClose} className="flex-1 py-3 border border-[#E4E4E7] font-medium hover:bg-[#F4F4F5]">
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading || !cardReady}
          className="flex-1 py-3 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
        >
          {loading ? 'Guardando...' : 'Guardar tarjeta'}
        </button>
      </div>

      <p className="text-xs text-[#A1A1AA] text-center flex items-center justify-center gap-1">
        <ShieldCheck size={14} /> Pagos seguros con Stripe
      </p>
    </form>
  );
}

// Analytics, Team, Settings pages remain the same
// Chart palette — CVD-validated (ámbar/azul/verde pasan separación protan/deutan/tritan)
const CHART_COLORS = { in: '#D97706', bot: '#0047FF', human: '#16A34A', wa: '#25D366', web: '#6366F1' };
const CHART_TICK = { fontSize: 11, fill: '#A1A1AA' };
const fmtDay = (d) => new Date(d + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });

function ChartTooltip({ active, payload, label, labelFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-xl shadow-lg px-3 py-2 text-xs">
      {label != null && (
        <p className="font-medium text-[#09090B] mb-1">{labelFormatter ? labelFormatter(label) : label}</p>
      )}
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="flex items-center gap-2 py-0.5">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-[#52525B]">{p.name}:</span>
          <span className="font-semibold text-[#09090B]">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-[#E4E4E7] p-6 ${className}`}>
      <h3 className="font-bold text-[#09090B]">{title}</h3>
      {subtitle && <p className="text-xs text-[#52525B] mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function EmptyChart() {
  return <div className="h-56 flex items-center justify-center text-sm text-[#A1A1AA]">Aún no hay datos en este período</div>;
}

function LegendRow({ items }) {
  return (
    <div className="flex flex-wrap items-center gap-4 mb-3">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5 text-xs text-[#52525B]">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: it.color }} /> {it.label}
        </span>
      ))}
    </div>
  );
}

export function Analytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.getAnalytics(days);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) toast.error('Error al cargar analíticas');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const k = data?.kpis;

  // Shift UTC hour buckets to the viewer's timezone
  const hourlyLocal = (() => {
    if (!data?.hourly_utc) return [];
    const off = Math.round(new Date().getTimezoneOffset() / 60);
    const buckets = Array.from({ length: 24 }, (_, h) => ({ hour: h, label: `${h}h`, count: 0 }));
    data.hourly_utc.forEach(({ hour, count }) => {
      buckets[(hour - off + 24) % 24].count += count;
    });
    return buckets;
  })();

  const botHumanData = k ? [
    { name: 'Bot', value: k.bot_replies, fill: CHART_COLORS.bot },
    { name: 'Humano', value: k.human_replies, fill: CHART_COLORS.human },
  ] : [];
  const channelData = data && !data.gated ? [
    { name: 'WhatsApp', value: data.channels.whatsapp, fill: CHART_COLORS.wa },
    { name: 'Web Chat', value: data.channels.web, fill: CHART_COLORS.web },
  ] : [];

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-[#09090B] mb-1">Analíticas</h1>
          <p className="text-[#52525B]">Métricas de tus conversaciones y del desempeño del bot</p>
        </div>
        <div className="flex border border-[#E4E4E7] rounded-xl overflow-hidden">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                days === d ? 'bg-[#0047FF] text-white' : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
              }`}
            >
              {d} días
            </button>
          ))}
        </div>
      </div>

      {/* KPIs (todos los planes) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Conversaciones activas', sub: 'últimos 7 días', value: k?.active_conversations ?? 0 },
          { label: 'Mensajes', sub: `últimos ${data?.days ?? days} días`, value: k?.messages_period ?? 0 },
          { label: 'Atendido por el bot', sub: `${k?.bot_replies ?? 0} bot · ${k?.human_replies ?? 0} humano`, value: `${k?.bot_pct ?? 0}%` },
          { label: 'Contactos nuevos', sub: `últimos ${data?.days ?? days} días`, value: k?.new_contacts ?? 0 },
        ].map((t) => (
          <div key={t.label} className="p-6 bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#52525B]">{t.label}</p>
            <p className="font-black text-3xl text-[#09090B] mt-1">{t.value}</p>
            <p className="text-xs text-[#A1A1AA] mt-1">{t.sub}</p>
          </div>
        ))}
      </div>

      {data?.gated ? (
        <div className="relative bg-white rounded-2xl shadow-sm border border-[#E4E4E7] p-10 text-center overflow-hidden">
          <div aria-hidden="true" className="absolute inset-0 opacity-40 pointer-events-none flex items-end gap-2 px-10 pb-0 blur-[2px]">
            {[35, 55, 40, 70, 52, 80, 62, 90, 74, 60, 85, 96].map((h, i) => (
              <div key={i} className="flex-1 rounded-t-md bg-[#0047FF]/30" style={{ height: `${h}%` }} />
            ))}
          </div>
          <div className="relative">
            <h3 className="font-bold text-xl text-[#09090B] mb-2">Desbloquea las gráficas detalladas</h3>
            <p className="text-[#52525B] mb-6 max-w-md mx-auto">
              Mensajes por día, % atendido por el bot, horas pico, escalaciones y más — disponibles en los planes <span className="font-semibold">Pro</span> y <span className="font-semibold">Business</span>.
            </p>
            <button
              onClick={() => navigate('/dashboard/billing')}
              className="bg-[#0047FF] text-white rounded-xl px-6 py-3 font-semibold hover:bg-[#0035CC] transition-colors"
            >
              Ver planes
            </button>
          </div>
        </div>
      ) : data && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Mensajes por día */}
          <ChartCard title="Mensajes por día" subtitle="Entrantes de clientes vs respuestas del bot y de tu equipo" className="lg:col-span-2">
            <LegendRow items={[
              { label: 'Entrantes', color: CHART_COLORS.in },
              { label: 'Bot', color: CHART_COLORS.bot },
              { label: 'Humano', color: CHART_COLORS.human },
            ]} />
            {k.messages_period === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.messages_daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#E4E4E7" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={CHART_TICK} tickLine={false} axisLine={false} minTickGap={28} />
                  <YAxis tick={CHART_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                  <RTooltip content={<ChartTooltip labelFormatter={fmtDay} />} />
                  <Line name="Entrantes" type="monotone" dataKey="contact" stroke={CHART_COLORS.in} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line name="Bot" type="monotone" dataKey="bot" stroke={CHART_COLORS.bot} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line name="Humano" type="monotone" dataKey="human" stroke={CHART_COLORS.human} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Bot vs Humano */}
          <ChartCard title="¿Quién responde?" subtitle="Respuestas del bot vs tu equipo">
            {(k.bot_replies + k.human_replies) === 0 ? <EmptyChart /> : (
              <div className="relative">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={botHumanData} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="88%" paddingAngle={2} stroke="#FFFFFF" strokeWidth={2}>
                      {botHumanData.map((e) => <Cell key={e.name} fill={e.fill} />)}
                    </Pie>
                    <RTooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <p className="font-black text-3xl text-[#09090B]">{k.bot_pct}%</p>
                  <p className="text-xs text-[#52525B]">bot</p>
                </div>
              </div>
            )}
            <div className="flex justify-center gap-6 mt-2">
              {botHumanData.map((e) => (
                <span key={e.name} className="inline-flex items-center gap-1.5 text-xs text-[#52525B]">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: e.fill }} />
                  {e.name}: <span className="font-semibold text-[#09090B]">{e.value}</span>
                </span>
              ))}
            </div>
          </ChartCard>

          {/* Horas pico */}
          <ChartCard title="Horas pico" subtitle="A qué hora escriben tus clientes (hora local)" className="lg:col-span-2">
            {hourlyLocal.every((h) => h.count === 0) ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={hourlyLocal} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#E4E4E7" />
                  <XAxis dataKey="label" tick={CHART_TICK} tickLine={false} axisLine={false} interval={2} />
                  <YAxis tick={CHART_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                  <RTooltip content={<ChartTooltip />} cursor={{ fill: '#F4F4F5' }} />
                  <Bar name="Mensajes" dataKey="count" fill={CHART_COLORS.bot} radius={[4, 4, 0, 0]} maxBarSize={16} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Canales */}
          <ChartCard title="Canales" subtitle="Origen de las conversaciones del período">
            {(channelData[0]?.value || 0) + (channelData[1]?.value || 0) === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={channelData} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="88%" paddingAngle={2} stroke="#FFFFFF" strokeWidth={2}>
                    {channelData.map((e) => <Cell key={e.name} fill={e.fill} />)}
                  </Pie>
                  <RTooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="flex justify-center gap-6 mt-2">
              {channelData.map((e) => (
                <span key={e.name} className="inline-flex items-center gap-1.5 text-xs text-[#52525B]">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: e.fill }} />
                  {e.name}: <span className="font-semibold text-[#09090B]">{e.value}</span>
                </span>
              ))}
            </div>
          </ChartCard>

          {/* Conversaciones nuevas */}
          <ChartCard title="Conversaciones nuevas" subtitle="Clientes que iniciaron conversación por día" className="lg:col-span-2">
            {data.conversations_daily.every((d) => d.count === 0) ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.conversations_daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#E4E4E7" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={CHART_TICK} tickLine={false} axisLine={false} minTickGap={28} />
                  <YAxis tick={CHART_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                  <RTooltip content={<ChartTooltip labelFormatter={fmtDay} />} />
                  <Line name="Conversaciones" type="monotone" dataKey="count" stroke={CHART_COLORS.bot} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Escalaciones */}
          <ChartCard title="Solicitudes de humano" subtitle={`${data.escalations_total} en el período`}>
            {data.escalations_total === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.escalations_daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#E4E4E7" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={CHART_TICK} tickLine={false} axisLine={false} minTickGap={28} />
                  <YAxis tick={CHART_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                  <RTooltip content={<ChartTooltip labelFormatter={fmtDay} />} cursor={{ fill: '#F4F4F5' }} />
                  <Bar name="Escalaciones" dataKey="count" fill={CHART_COLORS.in} radius={[4, 4, 0, 0]} maxBarSize={16} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      )}
    </div>
  );
}

export function Team() {
  const { company } = useOutletContext();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // member being edited, or null = create
  const [form, setForm] = useState({ name: '', email: '', password: '', company_role: 'agent' });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const plan = company?.plan || 'free';
  const teamEnabled = plan === 'pro' || plan === 'business';
  const memberLimit = plan === 'pro' ? 3 : null;

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await api.getTeam();
      setMembers(res.data);
    } catch {
      toast.error('Error al cargar el equipo');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', company_role: 'agent' });
    setShowModal(true);
  };

  const openEdit = (member) => {
    setEditing(member);
    setForm({ name: member.name, email: member.email, password: '', company_role: member.company_role });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('El nombre es obligatorio');
    if (!editing && !form.email.trim()) return toast.error('El correo es obligatorio');
    if (!editing && form.password.length < 6) return toast.error('La contraseña debe tener al menos 6 caracteres');

    setSaving(true);
    try {
      if (editing) {
        await api.updateTeamMember(editing.id, {
          name: form.name.trim(),
          company_role: form.company_role,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success('Miembro actualizado');
      } else {
        await api.createTeamMember({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          company_role: form.company_role,
        });
        toast.success(`${form.name.trim()} agregado al equipo`);
      }
      setShowModal(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteTeamMember(confirmDelete.id);
      toast.success('Miembro eliminado');
      setConfirmDelete(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-[#09090B] mb-2">Equipo</h1>
          <p className="text-[#52525B]">
            Miembros que atienden conversaciones cuando un cliente pide hablar con una persona
            {memberLimit ? ` · ${members.length} de ${memberLimit} miembros` : ''}
          </p>
        </div>
        {teamEnabled && (
          <button
            onClick={openCreate}
            data-testid="add-member-btn"
            className="flex items-center gap-2 bg-[#0047FF] text-white rounded-xl px-4 py-2.5 text-sm font-semibold hover:bg-[#0035CC] transition-colors"
          >
            <Plus size={18} /> Agregar miembro
          </button>
        )}
      </div>

      {!teamEnabled ? (
        <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] p-10 text-center max-w-xl mx-auto">
          <Users size={48} className="mx-auto text-[#0047FF] mb-4" />
          <h3 className="font-bold text-xl text-[#09090B] mb-2">Trabaja en equipo</h3>
          <p className="text-[#52525B] mb-6">
            Agrega miembros a tu equipo para que atiendan a tus clientes cuando pidan hablar con una persona.
            Disponible en los planes <span className="font-semibold">Pro</span> (3 miembros) y <span className="font-semibold">Business</span> (ilimitado).
          </p>
          <button
            onClick={() => navigate('/dashboard/billing')}
            className="bg-[#0047FF] text-white rounded-xl px-6 py-3 font-semibold hover:bg-[#0035CC] transition-colors"
          >
            Ver planes
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E4E4E7] text-left">
                  <th className="px-6 py-4 font-mono text-xs tracking-[0.1em] uppercase text-[#52525B]">Nombre</th>
                  <th className="px-6 py-4 font-mono text-xs tracking-[0.1em] uppercase text-[#52525B]">Correo</th>
                  <th className="px-6 py-4 font-mono text-xs tracking-[0.1em] uppercase text-[#52525B]">Rol</th>
                  <th className="px-6 py-4 font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-[#E4E4E7] last:border-b-0">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#0047FF]/10 text-[#0047FF] flex items-center justify-center font-bold text-sm">
                          {(m.name || m.email)[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-[#09090B]">
                          {m.name || '—'}
                          {m.id === user?.id && <span className="ml-2 text-xs text-[#A1A1AA]">(tú)</span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#52525B]">{m.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        m.company_role === 'owner' ? 'bg-[#0047FF]/10 text-[#0047FF]' : 'bg-green-100 text-green-700'
                      }`}>
                        {m.company_role === 'owner' ? 'Dueño' : 'Agente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEdit(m)}
                          className="p-2 text-[#52525B] hover:text-[#0047FF] hover:bg-[#F4F4F5] rounded-lg transition-colors"
                          title="Editar"
                        >
                          <PencilSimple size={16} />
                        </button>
                        {m.id !== user?.id && (
                          <button
                            onClick={() => setConfirmDelete(m)}
                            className="p-2 text-[#52525B] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit member modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border border-[#E4E4E7] w-full max-w-md shadow-2xl">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <h3 className="font-bold text-[#09090B]">{editing ? 'Editar miembro' : 'Agregar miembro'}</h3>
              <button onClick={() => setShowModal(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nombre del miembro"
                  data-testid="member-name"
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Correo <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="correo@empresa.com"
                  disabled={!!editing}
                  data-testid="member-email"
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none disabled:bg-[#F4F4F5] disabled:text-[#A1A1AA]"
                />
              </div>
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Contraseña {editing ? <span className="normal-case text-[#A1A1AA]">(deja vacío para no cambiarla)</span> : <span className="text-red-500">*</span>}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  data-testid="member-password"
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none"
                />
                {!editing && (
                  <p className="text-xs text-[#A1A1AA] mt-1">Compártela con el miembro; podrá iniciar sesión en novagente.io con su correo.</p>
                )}
              </div>
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Rol</label>
                <select
                  value={form.company_role}
                  onChange={(e) => setForm({ ...form, company_role: e.target.value })}
                  disabled={editing?.id === user?.id}
                  data-testid="member-role"
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white disabled:bg-[#F4F4F5]"
                >
                  <option value="agent">Agente — atiende conversaciones (solo Inbox y Contactos)</option>
                  <option value="owner">Dueño — acceso completo</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                disabled={saving}
                className="flex-1 py-2.5 border border-[#E4E4E7] rounded-xl text-[#09090B] font-medium hover:bg-[#F4F4F5] disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="member-save"
                className="flex-1 py-2.5 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
              >
                {saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Agregar miembro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        open={!!confirmDelete}
        title="Eliminar miembro"
        confirmLabel="Eliminar"
        danger
        processing={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      >
        <p className="text-sm text-[#52525B]">
          Vas a eliminar a <span className="font-medium text-[#09090B]">{confirmDelete?.name || confirmDelete?.email}</span>.
          Perderá acceso al dashboard y sus conversaciones asignadas volverán al equipo.
        </p>
      </ConfirmModal>
    </div>
  );
}

// WhatsApp message templates (per-company, with Meta approval flow)
const TEMPLATE_STATUS = {
  approved: { label: 'Aprobada', cls: 'bg-green-100 text-green-700' },
  received: { label: 'En revisión', cls: 'bg-yellow-100 text-yellow-700' },
  pending: { label: 'En revisión', cls: 'bg-yellow-100 text-yellow-700' },
  rejected: { label: 'Rechazada', cls: 'bg-red-100 text-red-700' },
  unsubmitted: { label: 'Sin enviar', cls: 'bg-[#F4F4F5] text-[#52525B]' },
};

function WhatsAppTemplatesCard({ plan }) {
  const navigate = useNavigate();
  const templatesEnabled = plan === 'pro' || plan === 'business';
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const emptyForm = { name: '', category: 'MARKETING', language: 'es_MX', content_type: 'text', body: '', samples: {}, media_url: '', actions: [] };
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const mediaInputRef = useRef(null);

  // Variables detected live in the body ({{1}}, {{2}}...)
  const bodyVars = [...new Set((form.body.match(/\{\{(\w+)\}\}/g) || []).map((m) => m.replace(/[{}]/g, '')))];

  const addVariable = () => {
    const nums = bodyVars.filter((v) => /^\d+$/.test(v)).map(Number);
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    setForm((prev) => ({ ...prev, body: `${prev.body}{{${next}}}` }));
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaUploading(true);
    try {
      const res = await api.uploadFile(file);
      setForm((prev) => ({ ...prev, media_url: res.data.media_url }));
    } catch {
      toast.error('Error al subir el archivo');
    } finally {
      setMediaUploading(false);
    }
  };

  const updateAction = (idx, patch) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    }));
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.getTemplates(true);
      setTemplates(res.data.templates || []);
    } catch {
      toast.error('Error al cargar las plantillas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (templatesEnabled) fetchTemplates();
    else setLoading(false);
  }, [templatesEnabled]);

  if (!templatesEnabled) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] mb-6 p-8 text-center">
        <h3 className="font-bold text-[#09090B] mb-2">Plantillas de WhatsApp</h3>
        <p className="text-sm text-[#52525B] max-w-md mx-auto mb-4">
          Crea plantillas aprobadas por Meta para iniciar conversaciones y recontactar clientes fuera
          de la ventana de 24 horas. Disponible en los planes <span className="font-semibold">Pro</span> y <span className="font-semibold">Business</span>.
        </p>
        <button
          onClick={() => navigate('/dashboard/billing')}
          className="bg-[#0047FF] text-white rounded-xl px-6 py-2.5 font-semibold hover:bg-[#0035CC] transition-colors"
        >
          Ver planes
        </button>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!form.name.trim() || !form.body.trim()) return toast.error('Nombre y cuerpo son obligatorios');
    const missingSamples = bodyVars.filter((v) => !(form.samples[v] || '').trim());
    if (missingSamples.length > 0) {
      return toast.error(`Meta exige un ejemplo para cada variable: ${missingSamples.map((v) => `{{${v}}}`).join(', ')}`);
    }
    if (form.content_type === 'media' && !form.media_url) {
      return toast.error('Sube una imagen o documento para la plantilla de media');
    }
    if (form.content_type === 'call-to-action' && form.actions.length === 0) {
      return toast.error('Agrega al menos un botón');
    }
    setCreating(true);
    try {
      await api.createTemplate({
        name: form.name,
        category: form.category,
        language: form.language,
        content_type: form.content_type,
        body: form.body,
        variables: form.samples,
        media_url: form.media_url || null,
        actions: form.content_type === 'call-to-action' ? form.actions : null,
      });
      toast.success('Plantilla creada y enviada a aprobación de Meta (suele tardar minutos u horas)');
      setShowCreate(false);
      setForm(emptyForm);
      fetchTemplates();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al crear la plantilla');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteTemplate(confirmDelete.sid);
      toast.success('Plantilla eliminada');
      setConfirmDelete(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] mb-6">
      <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between gap-4">
        <div>
          <h3 className="font-bold text-[#09090B] mb-1">Plantillas de WhatsApp</h3>
          <p className="text-sm text-[#52525B]">
            Para iniciar conversaciones o recontactar clientes fuera de la ventana de 24 horas. Meta debe aprobarlas.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={fetchTemplates}
            className="p-2 text-[#52525B] hover:text-[#0047FF] hover:bg-[#F4F4F5] rounded-lg transition-colors"
            title="Actualizar estados"
          >
            <ArrowClockwise size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="create-template-btn"
            className="flex items-center gap-2 bg-[#0047FF] text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#0035CC] transition-colors"
          >
            <Plus size={16} /> Nueva plantilla
          </button>
        </div>
      </div>
      <div className="p-6">
        {loading && templates.length === 0 ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-[#0047FF] border-t-transparent rounded-full" />
          </div>
        ) : templates.length === 0 ? (
          <p className="text-[#52525B] text-center py-6 text-sm">
            No tienes plantillas. Crea la primera para poder contactar clientes que no te han escrito.
          </p>
        ) : (
          <div className="space-y-3">
            {templates.map((t) => {
              const st = TEMPLATE_STATUS[t.status] || TEMPLATE_STATUS.unsubmitted;
              return (
                <div key={t.sid} className="border border-[#E4E4E7] rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-sm font-semibold text-[#09090B] truncate">{t.name}</span>
                      <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full flex-shrink-0 ${st.cls}`}>{st.label}</span>
                      {t.category && (
                        <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-[#F4F4F5] text-[#52525B] flex-shrink-0 lowercase">{t.category.toLowerCase()}</span>
                      )}
                    </div>
                    <button
                      onClick={() => setConfirmDelete(t)}
                      className="p-1.5 text-[#A1A1AA] hover:text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                      title="Eliminar plantilla"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                  <p className="text-sm text-[#52525B] whitespace-pre-wrap line-clamp-3">{t.body}</p>
                  {t.status === 'rejected' && t.rejection_reason && (
                    <p className="text-xs text-red-600 mt-2">Motivo del rechazo: {t.rejection_reason}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border border-[#E4E4E7] w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-[#09090B]">Nueva plantilla de WhatsApp</h3>
                <p className="text-xs text-[#52525B] mt-1">Meta la revisará antes de que puedas usarla</p>
              </div>
              <button onClick={() => setShowCreate(false)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="bienvenida_cliente"
                  data-testid="template-name"
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none font-mono text-sm"
                />
                <p className="text-xs text-[#A1A1AA] mt-1">Solo minúsculas, números y guiones bajos.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Categoría</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white text-sm"
                  >
                    <option value="MARKETING">Marketing — promos, invitaciones</option>
                    <option value="UTILITY">Utilidad — avisos, seguimientos</option>
                  </select>
                </div>
                <div>
                  <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Idioma</label>
                  <select
                    value={form.language}
                    onChange={(e) => setForm({ ...form, language: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none bg-white text-sm"
                  >
                    <option value="es_MX">Español (México)</option>
                    <option value="es">Español</option>
                    <option value="en_US">Inglés (EE.UU.)</option>
                    <option value="en">Inglés</option>
                    <option value="pt_BR">Portugués (Brasil)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">Tipo de contenido</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'text', label: 'Texto' },
                    { id: 'media', label: 'Imagen / Doc' },
                    { id: 'call-to-action', label: 'Botones' },
                  ].map((ct) => (
                    <button
                      key={ct.id}
                      type="button"
                      onClick={() => setForm({ ...form, content_type: ct.id })}
                      className={`py-2.5 text-sm font-medium rounded-xl border transition-colors ${
                        form.content_type === ct.id
                          ? 'border-[#0047FF] bg-[#0047FF]/5 text-[#0047FF]'
                          : 'border-[#E4E4E7] text-[#52525B] hover:border-[#0047FF]/40'
                      }`}
                    >
                      {ct.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-mono text-xs tracking-[0.1em] uppercase text-[#52525B]">
                    Mensaje <span className="text-red-500">*</span> <span className="normal-case text-[#A1A1AA]">{form.body.length}/1024</span>
                  </label>
                  <button
                    type="button"
                    onClick={addVariable}
                    className="text-xs text-[#0047FF] font-medium hover:underline"
                  >
                    + Agregar variable
                  </button>
                </div>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value.slice(0, 1024) })}
                  rows={5}
                  placeholder={'Hola {{1}}, te saluda el equipo de Mi Empresa...'}
                  data-testid="template-body"
                  className="w-full px-4 py-3 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
                />
                <p className="text-xs text-[#A1A1AA] mt-1">
                  Tip: en Marketing, incluir "Si prefieres no recibir mensajes, responde BAJA" mejora la aprobación.
                </p>
              </div>

              {/* Variable samples (required by Meta) */}
              {bodyVars.length > 0 && (
                <div className="bg-[#F4F4F5] rounded-xl p-4 space-y-3">
                  <p className="text-xs font-medium text-[#52525B]">
                    Meta exige un valor de ejemplo para cada variable (así revisan cómo se verá el mensaje real):
                  </p>
                  {bodyVars.map((v) => (
                    <div key={v} className="flex items-center gap-3">
                      <span className="font-mono text-sm text-[#0047FF] flex-shrink-0 w-14">{`{{${v}}}`}</span>
                      <input
                        type="text"
                        value={form.samples[v] || ''}
                        onChange={(e) => setForm({ ...form, samples: { ...form.samples, [v]: e.target.value } })}
                        placeholder={`Ejemplo: ${v === '1' ? 'Juan Pérez' : 'valor de muestra'}`}
                        data-testid={`template-sample-${v}`}
                        className="flex-1 px-3 py-2 rounded-lg border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm bg-white"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Media attachment */}
              {form.content_type === 'media' && (
                <div>
                  <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                    Imagen o documento <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-3">
                    {form.media_url && (
                      form.media_url.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                        <img src={form.media_url} alt="Media" className="w-16 h-16 object-cover rounded-xl border border-[#E4E4E7]" />
                      ) : (
                        <span className="text-xs text-[#52525B] bg-[#F4F4F5] rounded-lg px-3 py-2">Documento cargado ✓</span>
                      )
                    )}
                    <input type="file" ref={mediaInputRef} onChange={handleMediaUpload} accept="image/jpeg,image/png,.pdf" className="hidden" />
                    <button
                      type="button"
                      onClick={() => mediaInputRef.current?.click()}
                      disabled={mediaUploading}
                      className="px-4 py-2 border border-[#E4E4E7] rounded-xl text-sm font-medium hover:border-[#0047FF] hover:text-[#0047FF] transition-colors disabled:opacity-50"
                    >
                      {mediaUploading ? 'Subiendo...' : form.media_url ? 'Cambiar archivo' : 'Subir archivo'}
                    </button>
                  </div>
                  <p className="text-xs text-[#A1A1AA] mt-1">JPG, PNG o PDF. Se mostrará arriba del mensaje.</p>
                </div>
              )}

              {/* Call-to-action buttons */}
              {form.content_type === 'call-to-action' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="font-mono text-xs tracking-[0.1em] uppercase text-[#52525B]">
                      Botones <span className="text-red-500">*</span> <span className="normal-case text-[#A1A1AA]">(máx. 2)</span>
                    </label>
                    {form.actions.length < 2 && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, actions: [...form.actions, { type: 'URL', title: '', url: '', phone: '' }] })}
                        className="text-xs text-[#0047FF] font-medium hover:underline"
                      >
                        + Agregar botón
                      </button>
                    )}
                  </div>
                  {form.actions.length === 0 && (
                    <p className="text-xs text-[#A1A1AA]">Agrega un botón de enlace (ej. "Ver planes") o de llamada.</p>
                  )}
                  {form.actions.map((a, idx) => (
                    <div key={idx} className="bg-[#F4F4F5] rounded-xl p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <select
                          value={a.type}
                          onChange={(e) => updateAction(idx, { type: e.target.value })}
                          className="px-3 py-2 rounded-lg border border-[#E4E4E7] bg-white text-sm focus:outline-none"
                        >
                          <option value="URL">Enlace</option>
                          <option value="PHONE_NUMBER">Llamada</option>
                        </select>
                        <input
                          type="text"
                          value={a.title}
                          onChange={(e) => updateAction(idx, { title: e.target.value.slice(0, 25) })}
                          placeholder="Texto del botón (máx. 25)"
                          className="flex-1 px-3 py-2 rounded-lg border border-[#E4E4E7] bg-white text-sm focus:border-[#0047FF] focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, actions: form.actions.filter((_, i) => i !== idx) })}
                          className="p-2 text-[#A1A1AA] hover:text-red-500"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      {a.type === 'URL' ? (
                        <input
                          type="url"
                          value={a.url}
                          onChange={(e) => updateAction(idx, { url: e.target.value })}
                          placeholder="https://tuempresa.com/planes"
                          className="w-full px-3 py-2 rounded-lg border border-[#E4E4E7] bg-white text-sm focus:border-[#0047FF] focus:outline-none"
                        />
                      ) : (
                        <input
                          type="tel"
                          value={a.phone}
                          onChange={(e) => updateAction(idx, { phone: e.target.value })}
                          placeholder="+523311234567"
                          className="w-full px-3 py-2 rounded-lg border border-[#E4E4E7] bg-white text-sm focus:border-[#0047FF] focus:outline-none"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex gap-3">
              <button
                onClick={() => setShowCreate(false)}
                disabled={creating}
                className="flex-1 py-2.5 border border-[#E4E4E7] rounded-xl text-[#09090B] font-medium hover:bg-[#F4F4F5] disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                data-testid="template-save"
                className="flex-1 py-2.5 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC] disabled:opacity-50"
              >
                {creating ? 'Enviando...' : 'Crear y enviar a aprobación'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title="Eliminar plantilla"
        confirmLabel="Eliminar"
        danger
        processing={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      >
        <p className="text-sm text-[#52525B]">
          Vas a eliminar la plantilla <span className="font-mono font-medium text-[#09090B]">{confirmDelete?.name}</span>.
          Si estaba aprobada, dejarás de poder usarla para iniciar conversaciones.
        </p>
      </ConfirmModal>
    </div>
  );
}

export function Settings() {
  const { company } = useOutletContext();
  const [errorLogs, setErrorLogs] = useState([]);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  // Quick Replies
  const [quickReplies, setQuickReplies] = useState([]);
  const [newShortcut, setNewShortcut] = useState('');
  const [newContent, setNewContent] = useState('');
  const [addingReply, setAddingReply] = useState(false);
  // Webhooks
  const [webhooks, setWebhooks] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [addingWebhook, setAddingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  const SETTINGS_TABS = [
    { id: 'general', label: 'General' },
    { id: 'templates', label: 'Plantillas de WhatsApp' },
    { id: 'replies', label: 'Respuestas rápidas' },
    { id: 'webhooks', label: 'Webhooks' },
    { id: 'api', label: 'API' },
    { id: 'errors', label: 'Errores' },
  ];

  const WEBHOOK_EVENT_OPTIONS = [
    { id: 'message.received', label: 'Mensaje recibido' },
    { id: 'message.sent', label: 'Mensaje enviado (bot/agente)' },
    { id: 'conversation.created', label: 'Nueva conversación' },
    { id: 'conversation.escalated', label: 'Escalación a humano' },
    { id: 'contact.created', label: 'Nuevo contacto' },
    { id: 'contact.updated', label: 'Contacto actualizado' },
  ];

  const fetchWebhooks = async () => {
    try {
      const res = await api.getWebhooks();
      setWebhooks(res.data || []);
    } catch {}
  };

  const handleAddWebhook = async () => {
    if (!webhookUrl.trim() || webhookEvents.length === 0) return;
    setAddingWebhook(true);
    try {
      await api.createWebhook({ url: webhookUrl, events: webhookEvents });
      setWebhookUrl('');
      setWebhookEvents([]);
      toast.success('Webhook creado');
      fetchWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear webhook');
    } finally {
      setAddingWebhook(false);
    }
  };

  const handleDeleteWebhook = async (id) => {
    try {
      await api.deleteWebhook(id);
      toast.success('Webhook eliminado');
      fetchWebhooks();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleTestWebhook = async (id) => {
    setTestingWebhook(id);
    try {
      const res = await api.testWebhook(id);
      if (res.data.success) {
        toast.success(`Prueba enviada (status: ${res.data.status_code})`);
      } else {
        toast.error(`Error: ${res.data.error}`);
      }
    } catch {
      toast.error('Error al enviar prueba');
    } finally {
      setTestingWebhook(null);
    }
  };

  const toggleWebhookEvent = (eventId) => {
    setWebhookEvents(prev =>
      prev.includes(eventId) ? prev.filter(e => e !== eventId) : [...prev, eventId]
    );
  };

  useEffect(() => {
    fetchErrorLogs();
    fetchQuickReplies();
    fetchWebhooks();
    if (company) {
      setEmailNotifications(company.email_notifications !== false);
    }
  }, [company]);

  const fetchQuickReplies = async () => {
    try {
      const res = await api.getQuickReplies();
      setQuickReplies(res.data || []);
    } catch {}
  };

  const handleAddQuickReply = async () => {
    if (!newShortcut.trim() || !newContent.trim()) return;
    setAddingReply(true);
    try {
      await api.createQuickReply({ shortcut: newShortcut, content: newContent });
      setNewShortcut('');
      setNewContent('');
      toast.success('Respuesta rápida creada');
      fetchQuickReplies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear');
    } finally {
      setAddingReply(false);
    }
  };

  const handleDeleteQuickReply = async (id) => {
    try {
      await api.deleteQuickReply(id);
      toast.success('Eliminada');
      fetchQuickReplies();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const fetchErrorLogs = async () => {
    try {
      const res = await api.getErrorLogs(50);
      setErrorLogs(res.data.errors || []);
    } catch (error) {
      console.error('Error fetching error logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleNotificationToggle = async () => {
    const newValue = !emailNotifications;
    setEmailNotifications(newValue);
    try {
      await api.updateNotificationSettings({ email_notifications: newValue });
      toast.success(newValue ? 'Notificaciones por email activadas' : 'Notificaciones por email desactivadas');
    } catch (error) {
      setEmailNotifications(!newValue);
      toast.error('Error al actualizar configuración');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 lg:p-8">
      <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-[#09090B] mb-2">Configuración</h1>
      <p className="text-[#52525B] mb-6">Ajustes de tu cuenta</p>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {SETTINGS_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium rounded-xl whitespace-nowrap transition-colors ${
              activeTab === t.id
                ? 'bg-[#0047FF] text-white'
                : 'bg-white border border-[#E4E4E7] text-[#52525B] hover:text-[#09090B] hover:border-[#0047FF]/40'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'general' && (<>
      {/* Company Info */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] mb-6">
        <div className="p-6 border-b border-[#E4E4E7]">
          <h3 className="font-bold text-[#09090B] mb-4">Información de la empresa</h3>
          <div className="space-y-4">
            <div>
              <label className="block font-mono text-xs uppercase text-[#52525B] mb-2">Nombre</label>
              <input type="text" value={company?.name || ''} readOnly className="w-full px-4 py-3 border border-[#E4E4E7] bg-[#F4F4F5]" />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase text-[#52525B] mb-2">Email</label>
              <input type="email" value={company?.owner_email || ''} readOnly className="w-full px-4 py-3 border border-[#E4E4E7] bg-[#F4F4F5]" />
            </div>
          </div>
        </div>
        <div className="p-6">
          <h3 className="font-bold text-[#09090B] mb-4">Plan actual</h3>
          <p className="text-[#52525B] capitalize">{company?.plan || 'Free'} - {company?.subscription_status || 'Trial'}</p>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] mb-6">
        <div className="p-6">
          <h3 className="font-bold text-[#09090B] mb-4">Notificaciones</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-[#09090B]">Notificaciones por email</p>
              <p className="text-sm text-[#52525B]">Recibe alertas cuando el bot no pueda responder</p>
            </div>
            <button
              onClick={handleNotificationToggle}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                emailNotifications ? 'bg-[#0047FF]' : 'bg-[#E4E4E7]'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                emailNotifications ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>
      </>)}

      {/* Error Logs */}
      {activeTab === 'errors' && (
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
        <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
          <div>
            <h3 className="font-bold text-[#09090B]">Historial de errores</h3>
            <p className="text-sm text-[#52525B]">Errores del bot y problemas de respuesta</p>
          </div>
          <button onClick={fetchErrorLogs} className="text-sm text-[#0047FF] hover:underline">
            Actualizar
          </button>
        </div>
        <div className="divide-y divide-[#E4E4E7] max-h-96 overflow-y-auto">
          {loadingLogs ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-6 h-6 border-2 border-[#0047FF] border-t-transparent rounded-full mx-auto" />
            </div>
          ) : errorLogs.length === 0 ? (
            <div className="p-8 text-center">
              <Warning size={32} className="text-[#A1A1AA] mx-auto mb-2" />
              <p className="text-[#52525B]">No hay errores registrados</p>
              <p className="text-sm text-[#A1A1AA]">¡Tu bot está funcionando correctamente!</p>
            </div>
          ) : (
            errorLogs.map((log) => (
              <div key={log.id} className="p-4 hover:bg-[#F4F4F5]">
                <div className="flex items-start gap-3">
                  <Warning size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#09090B]">{log.title}</p>
                    <p className="text-sm text-[#52525B] line-clamp-2">{log.message}</p>
                    {log.metadata?.contact_phone && (
                      <p className="text-xs text-[#A1A1AA] mt-1">Cliente: {log.metadata.contact_phone}</p>
                    )}
                    {log.metadata?.error && (
                      <p className="text-xs text-red-400 mt-1 font-mono truncate">{log.metadata.error}</p>
                    )}
                    <p className="text-xs text-[#A1A1AA] mt-1">{formatDate(log.created_at)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      )}

      {/* WhatsApp Templates */}
      {activeTab === 'templates' && <WhatsAppTemplatesCard plan={company?.plan} />}

      {/* Quick Replies */}
      {activeTab === 'replies' && (
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] mb-6">
        <div className="p-6 border-b border-[#E4E4E7]">
          <h3 className="font-bold text-[#09090B] mb-1">Respuestas Rápidas</h3>
          <p className="text-sm text-[#52525B]">
            Crea plantillas para responder rápido en el chat. Usa <code className="bg-[#F4F4F5] px-1">/atajo</code> en el Inbox.
          </p>
        </div>
        <div className="p-6 space-y-4">
          {/* Add new */}
          <div className="flex gap-3">
            <input
              type="text"
              value={newShortcut}
              onChange={(e) => setNewShortcut(e.target.value.replace(/\s/g, '').toLowerCase())}
              placeholder="/saludo"
              className="w-32 px-3 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none font-mono text-sm"
            />
            <input
              type="text"
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Contenido de la respuesta..."
              className="flex-1 px-3 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
            />
            <button
              onClick={handleAddQuickReply}
              disabled={addingReply || !newShortcut.trim() || !newContent.trim()}
              className="px-4 py-2 bg-[#0047FF] text-white rounded-xl text-sm font-medium hover:bg-[#0035CC] disabled:opacity-50"
            >
              Agregar
            </button>
          </div>

          {/* List */}
          {quickReplies.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] py-4 text-center">No tienes respuestas rápidas. Crea una arriba.</p>
          ) : (
            <div className="space-y-2">
              {quickReplies.map((reply) => (
                <div key={reply.id} className="flex items-center gap-3 p-3 border border-[#E4E4E7]">
                  <span className="font-mono text-sm text-[#0047FF] font-bold w-28 flex-shrink-0">{reply.shortcut}</span>
                  <p className="flex-1 text-sm text-[#52525B] truncate">{reply.content}</p>
                  <button
                    onClick={() => handleDeleteQuickReply(reply.id)}
                    className="p-1 text-[#A1A1AA] hover:text-red-500"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      )}

      {/* Webhooks */}
      {activeTab === 'webhooks' && (
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] mb-6">
        <div className="p-6 border-b border-[#E4E4E7]">
          <h3 className="font-bold text-[#09090B] mb-1">Webhooks</h3>
          <p className="text-sm text-[#52525B]">
            Recibe notificaciones HTTP en tu servidor cuando ocurren eventos en Novagente.
          </p>
        </div>
        <div className="p-6 space-y-4">
          {/* Add new webhook */}
          <div className="space-y-3">
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://tuservidor.com/webhook"
              className="w-full px-3 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
            />
            <div className="flex flex-wrap gap-2">
              {WEBHOOK_EVENT_OPTIONS.map(ev => (
                <button
                  key={ev.id}
                  onClick={() => toggleWebhookEvent(ev.id)}
                  className={`px-3 py-1 text-xs border transition-colors ${
                    webhookEvents.includes(ev.id)
                      ? 'bg-[#0047FF] text-white rounded-xl border-[#0047FF]'
                      : 'border-[#E4E4E7] text-[#52525B] hover:border-[#0047FF]'
                  }`}
                >
                  {ev.label}
                </button>
              ))}
            </div>
            <button
              onClick={handleAddWebhook}
              disabled={addingWebhook || !webhookUrl.trim() || webhookEvents.length === 0}
              className="px-4 py-2 bg-[#0047FF] text-white rounded-xl text-sm font-medium hover:bg-[#0035CC] disabled:opacity-50"
            >
              Agregar webhook
            </button>
          </div>

          {/* List */}
          {webhooks.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] py-4 text-center">No tienes webhooks configurados.</p>
          ) : (
            <div className="space-y-3">
              {webhooks.map(wh => (
                <div key={wh.id} className="p-4 border border-[#E4E4E7] space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-mono text-[#09090B] truncate flex-1">{wh.url}</p>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleTestWebhook(wh.id)}
                        disabled={testingWebhook === wh.id}
                        className="px-2 py-1 text-xs border border-[#E4E4E7] hover:bg-[#F4F4F5] disabled:opacity-50"
                      >
                        {testingWebhook === wh.id ? 'Enviando...' : 'Probar'}
                      </button>
                      <button onClick={() => handleDeleteWebhook(wh.id)} className="p-1 text-[#A1A1AA] hover:text-red-500">
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {wh.events?.map(ev => (
                      <span key={ev} className="px-2 py-0.5 text-xs bg-[#F4F4F5] text-[#52525B]">{ev}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#A1A1AA]">Secret:</span>
                    <code className="text-xs bg-[#F4F4F5] px-2 py-0.5 font-mono text-[#52525B]">{wh.secret}</code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(wh.secret); toast.success('Secret copiado'); }}
                      className="text-xs text-[#0047FF] hover:underline"
                    >
                      Copiar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {activeTab === 'api' && <ApiKeysCard plan={company?.plan} />}
    </div>
  );
}

// ==================== API KEYS (Public API) ====================

function ApiKeysCard({ plan }) {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // full key, shown only once
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const hasAccess = plan === 'pro' || plan === 'business';
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/v1/api`;

  const fetchKeys = async () => {
    try {
      const res = await api.getApiKeys();
      setKeys(res.data.api_keys || []);
    } catch {}
  };

  useEffect(() => {
    if (hasAccess) fetchKeys();
  }, [hasAccess]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const res = await api.createApiKey({ name: name.trim() });
      setNewKey(res.data);
      setName('');
      fetchKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await api.revokeApiKey(id);
      toast.success('API key revocada');
      setConfirmRevoke(null);
      fetchKeys();
    } catch {
      toast.error('Error al revocar');
    }
  };

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const CurlBlock = ({ title, code }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-mono uppercase text-[#52525B]">{title}</p>
        <button onClick={() => copy(code, 'Ejemplo copiado')} className="text-xs text-[#0047FF] hover:underline">Copiar</button>
      </div>
      <pre className="bg-[#09090B] text-[#E4E4E7] text-xs p-4 rounded-xl overflow-x-auto whitespace-pre">{code}</pre>
    </div>
  );

  if (!hasAccess) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7] p-8 text-center">
        <Sparkle size={32} className="mx-auto text-[#0047FF] mb-3" />
        <h3 className="font-bold text-[#09090B] mb-2">API de envíos de Novagente</h3>
        <p className="text-sm text-[#52525B] mb-4 max-w-md mx-auto">
          Envía plantillas y mensajes de WhatsApp desde tus propios sistemas (CRM, tienda en línea, ERP)
          con la API de Novagente. Disponible en los planes Pro y Business.
        </p>
        <a href="/dashboard/billing" className="inline-block px-4 py-2 bg-[#0047FF] text-white rounded-xl text-sm font-medium hover:bg-[#0035CC]">
          Actualizar plan
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Keys management */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
        <div className="p-6 border-b border-[#E4E4E7]">
          <h3 className="font-bold text-[#09090B] mb-1">API keys</h3>
          <p className="text-sm text-[#52525B]">
            Autentican las peticiones a la API de Novagente. Envíalas en el header <code className="bg-[#F4F4F5] px-1.5 py-0.5 rounded font-mono text-xs">X-API-Key</code>. Máximo 5 keys activas.
          </p>
        </div>
        <div className="p-6 space-y-4">
          {/* New key shown ONCE */}
          {newKey && (
            <div className="p-4 bg-[#0047FF]/5 border border-[#0047FF]/30 rounded-xl space-y-2">
              <p className="text-sm font-bold text-[#09090B]">Tu nueva API key "{newKey.name}"</p>
              <p className="text-xs text-[#52525B]">Cópiala ahora — por seguridad <strong>no se volverá a mostrar</strong>.</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border border-[#E4E4E7] px-3 py-2 rounded-xl font-mono text-[#09090B] overflow-x-auto whitespace-nowrap">{newKey.api_key}</code>
                <button
                  onClick={() => copy(newKey.api_key, 'API key copiada')}
                  className="px-3 py-2 bg-[#0047FF] text-white rounded-xl text-xs font-medium hover:bg-[#0035CC] whitespace-nowrap"
                >
                  Copiar
                </button>
              </div>
              <button onClick={() => setNewKey(null)} className="text-xs text-[#52525B] hover:text-[#09090B] underline">Ya la guardé, ocultar</button>
            </div>
          )}

          {/* Create */}
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="Nombre de la key (ej. Mi CRM)"
              maxLength={60}
              className="flex-1 px-3 py-2 rounded-xl border border-[#E4E4E7] focus:border-[#0047FF] focus:outline-none text-sm"
            />
            <button
              onClick={handleCreate}
              disabled={creating || !name.trim()}
              className="px-4 py-2 bg-[#0047FF] text-white rounded-xl text-sm font-medium hover:bg-[#0035CC] disabled:opacity-50 whitespace-nowrap"
            >
              {creating ? 'Generando...' : 'Generar API key'}
            </button>
          </div>

          {/* List */}
          {keys.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] py-4 text-center">Aún no tienes API keys. Genera la primera para integrarte.</p>
          ) : (
            <div className="space-y-3">
              {keys.map((k) => (
                <div key={k.id} className="p-4 border border-[#E4E4E7] rounded-xl flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#09090B]">{k.name}</p>
                    <p className="text-xs font-mono text-[#52525B]">{k.prefix}</p>
                    <p className="text-xs text-[#A1A1AA]">
                      Creada {fmtDate(k.created_at)} · Último uso: {k.last_used_at ? fmtDate(k.last_used_at) : 'nunca'}
                    </p>
                  </div>
                  {confirmRevoke === k.id ? (
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleRevoke(k.id)} className="px-3 py-1.5 bg-red-600 text-white rounded-xl text-xs font-medium hover:bg-red-700">
                        Sí, revocar
                      </button>
                      <button onClick={() => setConfirmRevoke(null)} className="px-3 py-1.5 border border-[#E4E4E7] rounded-xl text-xs text-[#52525B] hover:bg-[#F4F4F5]">
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmRevoke(k.id)} className="px-3 py-1.5 border border-[#E4E4E7] rounded-xl text-xs text-[#52525B] hover:text-red-600 hover:border-red-300">
                      Revocar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Inline docs */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
        <div className="p-6 border-b border-[#E4E4E7]">
          <h3 className="font-bold text-[#09090B] mb-1">Documentación rápida</h3>
          <p className="text-sm text-[#52525B]">
            Base URL: <code className="bg-[#F4F4F5] px-1.5 py-0.5 rounded font-mono text-xs">{API_BASE}</code> · Límite: 60 peticiones por minuto por key ·
            Las respuestas de tus contactos te llegan por los <strong>Webhooks</strong> (pestaña anterior) con el evento <code className="bg-[#F4F4F5] px-1.5 py-0.5 rounded font-mono text-xs">message.received</code>.
          </p>
        </div>
        <div className="p-6 space-y-5">
          <CurlBlock
            title="1. Listar tus plantillas aprobadas"
            code={`curl ${API_BASE}/templates \\\n  -H "X-API-Key: ng_live_..."`}
          />
          <CurlBlock
            title="2. Enviar una plantilla (funciona siempre)"
            code={`curl -X POST ${API_BASE}/messages/template \\\n  -H "X-API-Key: ng_live_..." \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "to": "+5213311234567",\n    "template_sid": "HXxxxxxxxx",\n    "variables": { "1": "Juan" }\n  }'`}
          />
          <CurlBlock
            title="3. Enviar texto libre (ventana de 24h abierta)"
            code={`curl -X POST ${API_BASE}/messages/send \\\n  -H "X-API-Key: ng_live_..." \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "to": "+5213311234567",\n    "text": "Hola, tu pedido va en camino"\n  }'`}
          />
          <div className="text-xs text-[#52525B] space-y-1">
            <p><strong>Errores</strong> — llegan como <code className="bg-[#F4F4F5] px-1 rounded font-mono">{'{ "detail": { "code", "message" } }'}</code>:</p>
            <p><code className="bg-[#F4F4F5] px-1 rounded font-mono">window_closed</code> (409): el contacto no ha escrito en 24h — usa una plantilla. · <code className="bg-[#F4F4F5] px-1 rounded font-mono">rate_limited</code> (429): baja el ritmo. · <code className="bg-[#F4F4F5] px-1 rounded font-mono">invalid_api_key</code> (401): key inválida o revocada.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
